import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { useToast } from '@/components/ui/Toast';
import {
  useCreateBillingTransaction,
  TRANSACTION_TYPE_LABELS,
  type BillingTransactionType,
  type TransactionLineItem,
  type BillingTransactionPayload,
} from './hooks/useBillingTransaction';
import { useBillingEntityConfigs } from './hooks/useBillingEntityConfig';
import { useContactsList } from '@/features/contacts/hooks/useContacts';
import {
  useInvoicesList,
  useInvoiceDetail,
  useSendInvoice,
  useVoidInvoice,
  useSetBadDebt,
  useUpdateInvoice,
  billingKeys,
} from './hooks/useBilling';
import { useJobsList } from '@/features/jobs/hooks/useJobs';
import { useTaxRates } from '@/features/settings/hooks/useTaxRates';
import { useSystemLookups } from '@/features/settings/hooks/useSystemLookups';
import { useNoteCategories } from '@/features/contacts/hooks/useNoteCategories';
import { useStaffUsers } from '@/features/contacts/hooks/useStaffUsers';
import { usePrintInvoice } from './hooks/usePrintInvoice';
import { usePrintBillingDocument } from './hooks/usePrintBillingDocument';
import { usePermissions, PERMISSIONS } from '@/hooks/usePermissions';
import { GroupInvoicePicker } from '@/features/contact-groups';
import { useInvoiceNotes } from './hooks/useInvoiceNotes';
import { JobSearchModal, type SelectedJobWithItems } from './JobSearchModal';
import { PaymentRecordForm } from './PaymentRecordForm';
import { ClientSearchModal, type ClientSearchResult } from '@/components/ClientSearchModal';
import { StaffSearchModal, type StaffSearchResult } from '@/components/StaffSearchModal';
import { useAuthStore } from '@/features/auth/hooks/useAuth';
import { apiClient, type ApiResponse } from '@/lib/api-client';

const TRANSACTION_TYPES: BillingTransactionType[] = [
  'invoice',
  'quote',
  'advance',
  'credit_note',
  'debit_note',
  'write_off',
];

/** Which types use line items (vs a single amount) */
const LINE_ITEM_TYPES: BillingTransactionType[] = ['invoice', 'quote', 'advance'];

/** Which types need an invoice reference */
const INVOICE_REF_TYPES: BillingTransactionType[] = ['credit_note', 'write_off'];

/** Which types optionally accept an invoice reference */
const OPTIONAL_INVOICE_TYPES: BillingTransactionType[] = [];

/** Which types need a reason */
const REASON_TYPES: BillingTransactionType[] = [
  'credit_note',
  'debit_note',
  'write_off',
  'discount',
];

/**
 * Finds the configured tax rate whose percentage matches a stored line-item
 * percentage (rates may be stored as 0.13 or 13). Returns undefined for 0%/no
 * match so callers can decide how to treat it.
 */
function findTaxRateIdByPercent(
  taxRates: Array<{ id: string; rate: number }> | undefined,
  percent: number,
): string | undefined {
  if (!taxRates || !(percent > 0)) return undefined;
  const match = taxRates.find((r) => {
    const ratePercent = r.rate < 1 ? r.rate * 100 : r.rate;
    return Math.abs(ratePercent - percent) < 0.01;
  });
  return match?.id;
}

/** Effective tax percentage (0-100) for a line: chosen rate, else stored percentage. */
function lineTaxPercent(
  item: { taxRateId?: string | null; taxRatePercent?: number | null },
  taxRates: Array<{ id: string; rate: number }> | undefined,
): number {
  if (item.taxRateId) {
    const rate = (taxRates || []).find((r) => r.id === item.taxRateId);
    return rate ? (rate.rate < 1 ? rate.rate * 100 : rate.rate) : 0;
  }
  return typeof item.taxRatePercent === 'number' && item.taxRatePercent > 0
    ? item.taxRatePercent
    : 0;
}

/** Default empty line item */
function emptyLineItem(): TransactionLineItem {
  return { source: 'manual', description: '', quantity: 1, unitPrice: 0 };
}

/**
 * BillingTransactionForm — unified form for all billing transaction types.
 * Transaction type, practice entity, and client are chosen first.
 * The form body then adapts to show the relevant fields.
 *
 * Supports: Invoice, Quote, Credit Note, Debit Note, Write-Off, Discount
 */
export function BillingTransactionForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { invoiceId: editId } = useParams<{ invoiceId?: string }>();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const createMutation = useCreateBillingTransaction();
  const updateInvoiceMutation = useUpdateInvoice(editId ?? '');
  const isEditMode = !!editId;
  const { can, isAdmin } = usePermissions();

  // Invoice actions (edit mode only)
  const sendMutation = useSendInvoice();
  const voidMutation = useVoidInvoice();
  const badDebtMutation = useSetBadDebt();
  const printMutation = usePrintInvoice();
  const printCreditNoteMutation = usePrintBillingDocument('credit_note');
  const printDebitNoteMutation = usePrintBillingDocument('debit_note');
  const printQuoteMutation = usePrintBillingDocument('quote');
  const printWriteOffMutation = usePrintBillingDocument('write_off');
  const printAdvanceMutation = usePrintBillingDocument('advance');
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showAdvanceModal, setShowAdvanceModal] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [convertingQuote, setConvertingQuote] = useState(false);
  const [sendingQuote, setSendingQuote] = useState(false);
  const [acceptingQuote, setAcceptingQuote] = useState(false);
  const [decliningQuote, setDecliningQuote] = useState(false);
  const [voidingQuote, setVoidingQuote] = useState(false);

  // Fetch existing transaction when in edit mode (invoice or quote based on ?type param)
  const editType = (searchParams.get('type') as BillingTransactionType) || 'invoice';
  const { data: existingInvoice, isLoading: editLoading } = useInvoiceDetail(
    editType === 'invoice' && editId ? editId : '',
  );
  const { data: existingQuoteRaw, isLoading: _quoteEditLoading } = useQuery({
    queryKey: ['billing', 'quotes', 'detail', editId],
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: Record<string, unknown> }>(
        `/billing/quotes/${editId}`,
      );
      return response.data.data || null;
    },
    enabled: editType === 'quote' && !!editId,
  });

  const { data: existingAdvanceRaw } = useQuery({
    queryKey: ['billing', 'advances', 'detail', editId],
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: Record<string, unknown> }>(
        `/billing/advances/${editId}`,
      );
      return response.data.data || null;
    },
    enabled: editType === 'advance' && !!editId,
  });

  const { data: existingCreditNoteRaw } = useQuery({
    queryKey: ['billing', 'credit-notes', 'detail', editId],
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: Record<string, unknown> }>(
        `/billing/credit-notes/${editId}`,
      );
      return response.data.data || null;
    },
    enabled: editType === 'credit_note' && !!editId,
  });

  const { data: existingWriteOffRaw } = useQuery({
    queryKey: ['billing', 'write-offs', 'detail', editId],
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: Record<string, unknown> }>(
        `/billing/write-offs/${editId}`,
      );
      return response.data.data || null;
    },
    enabled: editType === 'write_off' && !!editId,
  });

  const { data: existingDebitNoteRaw } = useQuery({
    queryKey: ['billing', 'debit-notes', 'detail', editId],
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: Record<string, unknown> }>(
        `/billing/debit-notes/${editId}`,
      );
      return response.data.data || null;
    },
    enabled: editType === 'debit_note' && !!editId,
    staleTime: 0,
  });

  const { data: existingDiscountRaw } = useQuery({
    queryKey: ['billing', 'discounts', 'detail', editId],
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: Record<string, unknown> }>(
        `/billing/discounts/${editId}`,
      );
      return response.data.data || null;
    },
    enabled: editType === 'discount' && !!editId,
    staleTime: 0,
  });

  // ─── Header state ─────────────────────────────────────────────────────────
  const initialType = editType;
  const [txType, setTxType] = useState<BillingTransactionType>(initialType);
  const [practiceEntityCode, setPracticeEntityCode] = useState('');
  const [clientId, setClientId] = useState('');
  const [showGroupInvoice, setShowGroupInvoice] = useState(false);
  // Display name for the selected client. Kept separate from the contacts list
  // because the client search popup can return a client that isn't in the
  // (paginated) clientsData set used for address auto-fill.
  const [clientName, setClientName] = useState('');
  const [showClientSearchModal, setShowClientSearchModal] = useState(false);

  // The staff member ("Partner") this transaction is assigned to. Defaults to
  // the creating user but can be reassigned to another staff member; persisted
  // as created_by_id on the record.
  const currentUser = useAuthStore((s) => s.user);
  const [assignedToId, setAssignedToId] = useState('');
  const [assignedToName, setAssignedToName] = useState('');
  const [showStaffSearchModal, setShowStaffSearchModal] = useState(false);

  // ─── Data fetching ────────────────────────────────────────────────────────
  const { data: entityConfigs } = useBillingEntityConfigs();
  const { data: clientsData } = useContactsList({ contactTypeNature: 'client', pageSize: 200 });
  const { data: taxRates } = useTaxRates();
  const { data: serviceTypeLookups } = useSystemLookups('service_types');
  const { data: discountDefinitions } = useSystemLookups('discount_definitions', true);

  const entityOptions = (entityConfigs || []).map((c) => ({
    value: c.practiceEntityCode || c.practiceEntityId,
    label: c.billingEntityName || c.practiceEntityCode || c.practiceEntityId,
  }));

  const taxRateOptions = [
    { value: '', label: '0%' },
    ...(taxRates || [])
      .filter((r) => r.isActive)
      .map((r) => ({
        value: r.id,
        label: `${(r.rate < 1 ? r.rate * 100 : r.rate).toFixed(0)}%`,
      })),
  ];

  const serviceTypeOptions = [
    { value: '', label: 'Manual (free text)' },
    ...(serviceTypeLookups || [])
      .filter((s) => s.isActive)
      .map((s) => ({
        value: s.code,
        label: s.label,
      })),
  ];

  // ─── Entity config for defaults ───────────────────────────────────────────
  const selectedEntityConfig = (entityConfigs || []).find(
    (c) => (c.practiceEntityCode || c.practiceEntityId) === practiceEntityCode,
  );
  const defaultTaxRateId = selectedEntityConfig?.defaultTaxRateId || '';
  const requireDueDate = selectedEntityConfig?.requireDueDate ?? false;
  const currencySymbol = selectedEntityConfig?.currencySymbol || '$';

  // ─── Client jobs (for job-linked line items) ──────────────────────────────
  const { data: jobsData } = useJobsList({ clientId: clientId || undefined, pageSize: 100 });
  const jobOptions = (jobsData?.items ?? []).map(
    (j: { id: string; title?: string; jobType?: string }) => ({
      value: j.id,
      label: `${j.title || 'Untitled'} (${j.jobType || 'unknown type'})`,
      title: j.title || '',
      jobType: j.jobType || '',
    }),
  );

  // Header-level job selection (for invoice/quote — auto-populates line items)
  const [showJobSearchModal, setShowJobSearchModal] = useState(false);
  const [linkedJobInfo, setLinkedJobInfo] = useState<{
    id: string;
    title: string;
    jobCode: string | null;
    clientName?: string;
  } | null>(null);

  // When a job is selected at header level via the old dropdown (legacy — kept for backward compat)
  // The JobSearchModal handler (handleJobSearchSelect) handles populating line items directly.

  // ─── Job Search Modal handler ─────────────────────────────────────────────
  const handleJobSearchSelect = useCallback(
    (result: SelectedJobWithItems) => {
      setShowJobSearchModal(false);
      setLinkedJobInfo({
        id: result.job.id,
        title: result.job.title,
        jobCode: result.job.jobCode,
        clientName: result.job.clientName,
      });

      // Auto-set client if not already set
      if (!clientId && result.job.clientId) {
        setClientId(result.job.clientId);
        if (result.job.clientName) setClientName(result.job.clientName);
      }

      // Populate line items from billable items (WIP, time entries, or services)
      if (result.items.length > 0) {
        const newItems: TransactionLineItem[] = result.items.map((item) => ({
          source: (item.source === 'service' ? 'service' : 'job') as TransactionLineItem['source'],
          jobId: result.job.id,
          serviceTypeCode: item.source === 'service' ? item.description : undefined,
          description: item.description,
          quantity: item.hours > 0 ? item.hours : 1,
          unitPrice: item.amount,
          taxRateId: defaultTaxRateId || undefined,
        }));
        setLineItems(newItems);
      }
    },
    [clientId, defaultTaxRateId],
  );

  // In edit mode, backfill the client display name from whichever detail record
  // loaded. All billing detail endpoints attach a resolved `client_name`.
  useEffect(() => {
    if (!isEditMode) return;
    const raw = (existingInvoice ||
      existingQuoteRaw ||
      existingAdvanceRaw ||
      existingCreditNoteRaw ||
      existingWriteOffRaw ||
      existingDebitNoteRaw ||
      existingDiscountRaw) as Record<string, unknown> | null | undefined;
    if (!raw) return;
    const name = (raw.client_name || raw.clientName || '') as string;
    if (name) setClientName(name);

    // Backfill the assigned partner from the record's creator, if present.
    const createdBy = (raw.created_by_id || raw.createdById || '') as string;
    if (createdBy) setAssignedToId(createdBy);
    const createdByName = (raw.created_by_name || raw.createdByName || '') as string;
    if (createdByName) setAssignedToName(createdByName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isEditMode,
    existingInvoice,
    existingQuoteRaw,
    existingAdvanceRaw,
    existingCreditNoteRaw,
    existingWriteOffRaw,
    existingDebitNoteRaw,
    existingDiscountRaw,
  ]);

  // On create, default the assigned partner to the current user.
  useEffect(() => {
    if (!isEditMode && !assignedToId && currentUser?.id) {
      setAssignedToId(currentUser.id);
      setAssignedToName(currentUser.name || currentUser.email || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, currentUser?.id]);

  // Backfill the partner display name from the staff list when we only have the
  // id (e.g. in edit mode where the detail record carried no created_by_name).
  const handleStaffSearchSelect = useCallback((staff: StaffSearchResult) => {
    setAssignedToId(staff.id);
    setAssignedToName(staff.name || staff.email || '');
    setShowStaffSearchModal(false);
  }, []);

  // ─── Client Search Modal handler ──────────────────────────────────────────
  const handleClientSearchSelect = useCallback(
    (client: ClientSearchResult) => {
      setClientId(client.id);
      setClientName(client.displayName || client.fullName || '');
      // Clear any linked invoice reference since it's client-specific.
      setInvoiceId('');
      setLinkedInvoiceNumber('');
      setShowClientSearchModal(false);

      // Auto-initialize the Practice Entity from the client's default practice
      // entity (added to the contact form via the Form Builder). The stored value
      // may be a practice-entity code or id; match it against the entity configs
      // and set the Select value. The user can still change it afterwards.
      const clientPe = client.practiceEntityId;
      if (clientPe) {
        const matched = (entityConfigs || []).find(
          (c) => c.practiceEntityCode === clientPe || c.practiceEntityId === clientPe,
        );
        if (matched) {
          setPracticeEntityCode(matched.practiceEntityCode || matched.practiceEntityId);
        } else {
          // Fall back to the raw stored value (it already matches a Select option
          // value when the entity uses a code).
          setPracticeEntityCode(clientPe);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [entityConfigs],
  );

  // ─── Client invoices (for credit note / write-off reference) ─────────────
  const { data: invoicesData } = useInvoicesList({
    clientId: clientId || undefined,
    pageSize: 100,
  });
  const invoiceOptionsBase = invoicesData?.invoices ?? [];

  // ─── Form state ───────────────────────────────────────────────────────────
  const today = new Date().toISOString().split('T')[0];
  const [transactionDate, setTransactionDate] = useState(today);
  const [dueDate, setDueDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [invoiceId, setInvoiceId] = useState('');
  const [linkedInvoiceNumber, setLinkedInvoiceNumber] = useState('');
  const [amount, setAmount] = useState('');
  const [discountType, setDiscountType] = useState<'flat' | 'percentage'>('flat');
  const [reason, setReason] = useState('');
  const [serviceTypeCode, setServiceTypeCode] = useState('');
  const [lineItems, setLineItems] = useState<TransactionLineItem[]>([emptyLineItem()]);
  const [selectedDiscountId, setSelectedDiscountId] = useState('');
  // Set true after a submit attempt so blank line-item descriptions are
  // highlighted inline (avoids showing errors before the user acts).
  const [showLineItemErrors, setShowLineItemErrors] = useState(false);

  // Invoice reference options (computed after invoiceId state is available)
  const invoiceOptions = invoiceOptionsBase
    .filter(
      (i) =>
        i.clientId === clientId &&
        (['sent', 'viewed', 'partially_paid', 'overdue'].includes(i.status) || i.id === invoiceId),
    )
    .map((i) => ({
      value: i.id,
      label: `${i.invoiceNumber} — ${currencySymbol}${i.outstandingBalance.toFixed(2)} outstanding`,
    }));

  // Billing & postal address state
  const [billingAddress, setBillingAddress] = useState({
    addressLine1: '',
    addressLine2: '',
    city: '',
    province: '',
    postalCode: '',
    country: '',
  });
  const [postalAddress, setPostalAddress] = useState({
    addressLine1: '',
    addressLine2: '',
    city: '',
    province: '',
    postalCode: '',
    country: '',
  });
  const [postalSameAsBilling, setPostalSameAsBilling] = useState(true);
  const [showAddressModal, setShowAddressModal] = useState(false);

  // Notes with categories/reminders (saved after transaction creation)
  const { data: noteCategories } = useNoteCategories();
  const { data: staffUsers } = useStaffUsers();
  const { data: savedNotes } = useInvoiceNotes(editId ?? '');

  // If we have an assigned partner id but no display name yet, resolve it from
  // the staff list (covers edit-mode records without a created_by_name).
  useEffect(() => {
    if (assignedToId && !assignedToName) {
      const match = (staffUsers ?? []).find((u) => u.id === assignedToId);
      if (match) setAssignedToName(match.name || match.email || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedToId, assignedToName, staffUsers]);

  // Fetch applied advances for this invoice (edit mode only)
  const { data: appliedAdvances } = useQuery({
    queryKey: ['billing', 'invoices', editId, 'advance-applications'],
    queryFn: async () => {
      const response = await apiClient.get<{
        success: boolean;
        data: Array<{
          id: string;
          amount: number;
          applied_at: string;
          advance_number: string;
          advance_id: string;
        }>;
      }>(`/billing/invoices/${editId}/advance-applications`);
      return response.data.data || [];
    },
    enabled: isEditMode && editType === 'invoice' && !!editId,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  // Fetch credit notes for this invoice (edit mode only)
  const { data: invoiceCreditNotes } = useQuery({
    queryKey: ['billing', 'invoices', editId, 'credit-notes'],
    queryFn: async () => {
      const response = await apiClient.get<{
        success: boolean;
        data: Array<Record<string, unknown>>;
      }>(`/billing/invoices/${editId}/credit-notes`);
      return (response.data.data || []).map((cn) => ({
        id: cn.id as string,
        creditNoteNumber: (cn.credit_note_number || cn.creditNoteNumber || '') as string,
        amount: Number(cn.amount || 0),
        reason: (cn.reason || '') as string,
        status: (cn.status || 'applied') as string,
        createdAt: (cn.created_at || cn.createdAt || '') as string,
      }));
    },
    enabled: isEditMode && editType === 'invoice' && !!editId,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  // Fetch write-offs for this invoice (edit mode only)
  const { data: invoiceWriteOffs } = useQuery({
    queryKey: ['billing', 'invoices', editId, 'write-offs'],
    queryFn: async () => {
      const response = await apiClient.get<
        ApiResponse<{ writeOffs: Array<Record<string, unknown>>; total: number }>
      >('/billing/write-offs', { params: { invoice_id: editId } });
      return (response.data.data.writeOffs || []).map((wo: Record<string, unknown>) => ({
        id: wo.id as string,
        writeOffNumber: (wo.write_off_number || wo.writeOffNumber || '') as string,
        amount: Number(wo.amount || 0),
        reason: (wo.reason || '') as string,
        status: (wo.status || 'applied') as string,
        createdAt: (wo.created_at || wo.createdAt || '') as string,
      }));
    },
    enabled: isEditMode && editType === 'invoice' && !!editId,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  interface PendingNote {
    category: string;
    content: string;
    reminderDate: string;
    reminderAssigneeId: string;
    clientVisible: boolean;
  }
  const [pendingNotes, setPendingNotes] = useState<PendingNote[]>([]);
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [newNoteCategory, setNewNoteCategory] = useState('');
  const [newNoteContent, setNewNoteContent] = useState('');
  const [newNoteReminderDate, setNewNoteReminderDate] = useState('');
  const [newNoteAssigneeId, setNewNoteAssigneeId] = useState('');
  const [newNoteAssigneeSearch, setNewNoteAssigneeSearch] = useState('');
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const [newNoteClientVisible, setNewNoteClientVisible] = useState(false);
  const [savingNote, setSavingNote] = useState(false);

  const selectedNoteCategoryObj = noteCategories?.find(
    (cat) => cat.name.toLowerCase().replace(/\s+/g, '_') === newNoteCategory,
  );
  const isSchedulingCategory = selectedNoteCategoryObj?.isScheduling ?? false;

  const handleAddPendingNote = async () => {
    if (!newNoteContent.trim()) return;
    if (isSchedulingCategory && !newNoteReminderDate) {
      toast({ variant: 'error', title: 'Reminder date is required for this category' });
      return;
    }

    // In edit mode, save directly to the API
    if (isEditMode && editId) {
      setSavingNote(true);
      try {
        await apiClient.post(`/billing/invoices/${editId}/notes`, {
          category: newNoteCategory || null,
          content: newNoteContent.trim(),
          reminder_date: isSchedulingCategory ? newNoteReminderDate : null,
          reminder_assignee_id: isSchedulingCategory ? newNoteAssigneeId || null : null,
          client_visible: newNoteClientVisible,
        });
        // Also save to billing_notes for portal visibility
        if (newNoteClientVisible) {
          const entityType =
            editType === 'credit_note'
              ? 'credit_note'
              : editType === 'debit_note'
                ? 'debit_note'
                : editType === 'write_off'
                  ? 'write_off'
                  : editType || txType;
          await apiClient.post('/billing/billing-notes', {
            entity_type: entityType,
            entity_id: editId,
            content: newNoteContent.trim(),
            client_visible: true,
            sender_type: 'staff',
          });
        }
        toast({ variant: 'success', title: 'Note saved' });
        // Refresh saved notes
        queryClient.invalidateQueries({ queryKey: ['billing', 'invoices', editId, 'notes'] });
      } catch {
        toast({ variant: 'error', title: 'Failed to save note' });
        setSavingNote(false);
        return;
      }
      setSavingNote(false);
    } else {
      // In create mode, queue notes for saving after transaction creation
      setPendingNotes((prev) => [
        ...prev,
        {
          category: newNoteCategory,
          content: newNoteContent.trim(),
          reminderDate: isSchedulingCategory ? newNoteReminderDate : '',
          reminderAssigneeId: isSchedulingCategory ? newNoteAssigneeId : '',
          clientVisible: newNoteClientVisible,
        },
      ]);
    }

    setNewNoteContent('');
    setNewNoteCategory('');
    setNewNoteReminderDate('');
    setNewNoteAssigneeId('');
    setNewNoteAssigneeSearch('');
    setNewNoteClientVisible(false);
    setShowNoteForm(false);
  };

  const removePendingNote = (idx: number) => {
    setPendingNotes((prev) => prev.filter((_, i) => i !== idx));
  };

  // Auto-compute due date when practice entity changes
  useEffect(() => {
    if (selectedEntityConfig && transactionDate) {
      const d = new Date(transactionDate);
      d.setDate(d.getDate() + (selectedEntityConfig.defaultPaymentTermsDays || 30));
      setDueDate(d.toISOString().split('T')[0]);
    }
  }, [selectedEntityConfig, transactionDate]);

  // For NEW quotes: auto-compute the expiry date from the entity's configured
  // quote expiry days (issue/transaction date + quote expiry days). Skipped in
  // edit mode so a saved expiry date is not overwritten on load.
  useEffect(() => {
    if (!isEditMode && txType === 'quote' && selectedEntityConfig && transactionDate) {
      const d = new Date(transactionDate);
      d.setDate(d.getDate() + (selectedEntityConfig.defaultQuoteExpiryDays ?? 30));
      setExpiryDate(d.toISOString().split('T')[0]);
    }
  }, [isEditMode, txType, selectedEntityConfig, transactionDate]);

  // Apply the selected practice entity's default tax rate to all line items.
  //
  // In CREATE mode, selecting a practice entity must auto-apply its configured
  // tax rate — including the user's very first selection. In EDIT mode, the
  // initial population (when `defaultTaxRateId` first resolves) must NOT clobber
  // the tax rates already saved on the invoice/quote line items; only a
  // subsequent deliberate change of entity re-applies. We track the previous
  // rate in a ref so we can tell a real change from the initial resolve.
  const previousDefaultTaxRateRef = useRef<string | null>(null);

  useEffect(() => {
    if (defaultTaxRateId && txType !== 'advance') {
      const isInitialPopulation = previousDefaultTaxRateRef.current === null;
      const isSameRate = previousDefaultTaxRateRef.current === defaultTaxRateId;

      previousDefaultTaxRateRef.current = defaultTaxRateId;

      // Nothing to do if the rate didn't actually change.
      if (isSameRate) {
        return;
      }

      // Preserve saved tax rates on the first resolve in EDIT mode only.
      // In create mode the first resolve IS the user's entity selection, so it
      // must apply (this was the regression: the first selection was skipped).
      if (isInitialPopulation && isEditMode) {
        return;
      }

      setLineItems((prev) =>
        prev.map((li) => ({ ...li, taxRateId: defaultTaxRateId, taxRatePercent: undefined })),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultTaxRateId, txType, isEditMode]);

  // Populate billing address when client changes
  useEffect(() => {
    if (clientId) {
      const selectedClient = (clientsData?.contacts ?? []).find((c) => c.id === clientId);
      if (selectedClient) {
        // Backfill the display name if we don't already have one (e.g. client
        // set programmatically from a linked job or in edit mode).
        if (!clientName) {
          const full = `${selectedClient.firstName ?? ''} ${selectedClient.lastName ?? ''}`.trim();
          setClientName(full || selectedClient.email || '');
        }
        const addr = {
          addressLine1: selectedClient.addressLine1 || '',
          addressLine2: selectedClient.addressLine2 || '',
          city: selectedClient.city || '',
          province: selectedClient.province || '',
          postalCode: selectedClient.postalCode || '',
          country: selectedClient.country || '',
        };
        setBillingAddress(addr);
        if (postalSameAsBilling) {
          setPostalAddress(addr);
        }
      }
    }
  }, [clientId]);

  // Populate form when in edit mode and data loads
  useEffect(() => {
    if (isEditMode && existingInvoice) {
      setTxType('invoice');
      setClientId(existingInvoice.clientId || '');

      // Find matching practice entity config by practiceEntityId from the raw response
      const rawInvoice = existingInvoice as unknown as Record<string, unknown>;
      const peId = (rawInvoice.practiceEntityId || rawInvoice.practice_entity_id || '') as string;
      const matchedConfig = (entityConfigs || []).find(
        (c) => c.practiceEntityId === peId || c.practiceEntityCode === peId,
      );
      if (matchedConfig) {
        setPracticeEntityCode(matchedConfig.practiceEntityCode || matchedConfig.practiceEntityId);
      } else if (peId) {
        // Try matching by billing_entity_id as fallback
        const billingEntityId = (rawInvoice.billingEntityId ||
          rawInvoice.billing_entity_id ||
          '') as string;
        const fallbackConfig = (entityConfigs || []).find(
          (c) => c.practiceEntityId === billingEntityId,
        );
        if (fallbackConfig) {
          setPracticeEntityCode(
            fallbackConfig.practiceEntityCode || fallbackConfig.practiceEntityId,
          );
        }
      }

      setTransactionDate(existingInvoice.issueDate ? existingInvoice.issueDate.split('T')[0] : '');
      setDueDate(existingInvoice.dueDate ? existingInvoice.dueDate.split('T')[0] : '');

      // Set linked job info if invoice has a job
      if (existingInvoice.jobId) {
        const rawInv = existingInvoice as unknown as Record<string, unknown>;
        const jobRef = (rawInv.jobReference || '') as string;
        // Parse job reference: "CODE Title | Client" or just use as-is
        const parts = jobRef.split(' | ');
        const codeAndTitle = parts[0] || '';
        const clientName = parts[1] || '';
        // Try to extract code (monospace portion before first space)
        const firstSpace = codeAndTitle.indexOf(' ');
        const jobCode = firstSpace > 0 ? codeAndTitle.slice(0, firstSpace) : null;
        const title = firstSpace > 0 ? codeAndTitle.slice(firstSpace + 1) : codeAndTitle;

        setLinkedJobInfo({
          id: existingInvoice.jobId,
          title: title || `Job ${existingInvoice.jobId.slice(0, 8)}`,
          jobCode: jobCode,
          clientName: clientName || undefined,
        });
      }

      // Populate line items
      if (existingInvoice.lineItems && existingInvoice.lineItems.length > 0) {
        setLineItems(
          existingInvoice.lineItems.map((li) => {
            // Preserve the line's stored tax (e.g. carried over from a quote).
            // Resolve it to a tax rate by percentage; if none matches, keep the
            // raw percentage so saving doesn't reset the line to 0%. The entity
            // default is NOT substituted — that would change the quoted tax.
            const storedPercent = Number(li.taxRatePercent ?? 0);
            const resolvedId = li.taxRateId || findTaxRateIdByPercent(taxRates, storedPercent);
            return {
              source: 'manual' as const,
              description: li.description,
              quantity: li.quantity,
              unitPrice: li.unitPrice,
              taxRateId: resolvedId,
              taxRatePercent: !resolvedId && storedPercent > 0 ? storedPercent : undefined,
              discountType: li.discountType || undefined,
              discountValue: li.discountValue || undefined,
            };
          }),
        );

        // Try to match discount category from line item discounts
        // (handled in separate useEffect to avoid race conditions)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, existingInvoice, entityConfigs]);

  // In edit mode the tax-rate list can load after the invoice. Once it is
  // available, resolve any line that only carries a stored percentage to its
  // matching rate. Touches only unresolved lines, so user edits are untouched.
  useEffect(() => {
    if (!isEditMode || !taxRates || taxRates.length === 0) return;
    setLineItems((prev) => {
      let changed = false;
      const next = prev.map((li) => {
        if (li.taxRateId || !li.taxRatePercent) return li;
        const id = findTaxRateIdByPercent(taxRates, li.taxRatePercent);
        if (!id) return li;
        changed = true;
        return { ...li, taxRateId: id, taxRatePercent: undefined };
      });
      return changed ? next : prev;
    });
  }, [taxRates, isEditMode, lineItems]);

  // Restore discount category from the saved discount_definition_id
  const discountMatchedRef = useRef(false);
  useEffect(() => {
    if (!isEditMode || !existingInvoice) return;
    if (discountMatchedRef.current) return;

    const savedDiscountId = existingInvoice.discountDefinitionId || '';

    if (savedDiscountId) {
      discountMatchedRef.current = true;
      setSelectedDiscountId(savedDiscountId);
    }
  }, [isEditMode, existingInvoice]);

  // Populate form when in edit mode for QUOTES
  useEffect(() => {
    if (isEditMode && editType === 'quote' && existingQuoteRaw) {
      setTxType('quote');
      const raw = existingQuoteRaw as Record<string, unknown>;
      setClientId((raw.client_id || raw.clientId || '') as string);
      setTransactionDate(
        raw.issue_date
          ? String(raw.issue_date).split('T')[0]
          : raw.created_at
            ? String(raw.created_at).split('T')[0]
            : '',
      );
      setExpiryDate(
        raw.valid_until
          ? String(raw.valid_until).split('T')[0]
          : raw.expiry_date
            ? String(raw.expiry_date).split('T')[0]
            : '',
      );

      // Load practice entity
      const peId = (raw.practice_entity_id || raw.practiceEntityId || '') as string;
      if (peId && entityConfigs) {
        const matchedConfig = entityConfigs.find(
          (c) => c.practiceEntityId === peId || c.practiceEntityCode === peId,
        );
        if (matchedConfig) {
          setPracticeEntityCode(matchedConfig.practiceEntityCode || matchedConfig.practiceEntityId);
        }
      }

      // Restore discount category
      const savedDiscountDefId = (raw.discount_definition_id ||
        raw.discountDefinitionId ||
        '') as string;
      if (savedDiscountDefId) {
        setSelectedDiscountId(savedDiscountDefId);
      } else {
        // Infer from line items: if any line has a discount, find 'manual' definition
        const lineItemsRaw = (raw.line_items || raw.lineItems || []) as Record<string, unknown>[];
        const hasDiscount = lineItemsRaw.some(
          (li) =>
            (li.discount_type || li.discountType) &&
            Number(li.discount_value ?? li.discountValue ?? 0) > 0,
        );
        if (hasDiscount && discountDefinitions) {
          const manualDef = discountDefinitions.find(
            (d) => d.code === 'manual' || d.label?.toLowerCase() === 'manual',
          );
          if (manualDef) {
            setSelectedDiscountId(manualDef.id);
          }
        }
      }

      // Populate line items
      const lineItemsRaw = (raw.line_items || raw.lineItems || []) as Record<string, unknown>[];
      if (lineItemsRaw.length > 0) {
        setLineItems(
          lineItemsRaw.map((li) => {
            // Resolve taxRateId from stored tax_rate percentage
            let resolvedTaxRateId = (li.tax_rate_id || li.taxRateId || undefined) as
              | string
              | undefined;
            if (!resolvedTaxRateId && li.tax_rate && taxRates) {
              const storedRate = Number(li.tax_rate);
              const match = taxRates.find((r) => {
                const ratePercent = r.rate < 1 ? r.rate * 100 : r.rate;
                return Math.abs(ratePercent - storedRate) < 0.01;
              });
              if (match) resolvedTaxRateId = match.id;
            }
            return {
              source: 'manual' as const,
              description: (li.description || '') as string,
              quantity: Number(li.quantity || 1),
              unitPrice: Number(li.unit_price || li.unitPrice || 0),
              taxRateId: resolvedTaxRateId,
              discountType: (li.discount_type ?? li.discountType ?? null) as
                | 'flat'
                | 'percentage'
                | null,
              discountValue: Number(li.discount_value ?? li.discountValue ?? 0),
            };
          }),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, editType, existingQuoteRaw, taxRates, entityConfigs, discountDefinitions]);

  // Populate form when in edit mode for ADVANCES
  useEffect(() => {
    if (isEditMode && editType === 'advance' && existingAdvanceRaw) {
      setTxType('advance');
      const raw = existingAdvanceRaw as Record<string, unknown>;
      setClientId((raw.client_id || raw.clientId || '') as string);
      setTransactionDate(
        raw.transaction_date
          ? String(raw.transaction_date).split('T')[0]
          : raw.created_at
            ? String(raw.created_at).split('T')[0]
            : '',
      );
      setDueDate(raw.due_date ? String(raw.due_date).split('T')[0] : '');
      setReason((raw.reason || '') as string);

      // Load job reference for advance
      const advJobId = (raw.job_id || raw.jobId || '') as string;
      const advJobRef = (raw.job_reference || raw.jobReference || '') as string;
      if (advJobId) {
        const parts = advJobRef.split(' | ');
        const codeAndTitle = parts[0] || '';
        const clientName = parts[1] || '';
        const firstSpace = codeAndTitle.indexOf(' ');
        const jobCode = firstSpace > 0 ? codeAndTitle.slice(0, firstSpace) : null;
        const title = firstSpace > 0 ? codeAndTitle.slice(firstSpace + 1) : codeAndTitle;

        setLinkedJobInfo({
          id: advJobId,
          title: title || `Job ${advJobId.slice(0, 8)}`,
          jobCode: jobCode,
          clientName: clientName || undefined,
        });
      }

      // Load practice entity
      const peId = (raw.practice_entity_id || raw.practiceEntityId || '') as string;
      if (peId && entityConfigs) {
        const matchedConfig = entityConfigs.find(
          (c) => c.practiceEntityId === peId || c.practiceEntityCode === peId,
        );
        if (matchedConfig) {
          setPracticeEntityCode(matchedConfig.practiceEntityCode || matchedConfig.practiceEntityId);
        }
      }

      // Populate line items
      const lineItemsRaw = (raw.line_items || raw.lineItems || []) as Record<string, unknown>[];
      if (lineItemsRaw.length > 0) {
        setLineItems(
          lineItemsRaw.map((li) => {
            let resolvedTaxRateId = (li.tax_rate_id || li.taxRateId || undefined) as
              | string
              | undefined;
            if (!resolvedTaxRateId && li.tax_rate && taxRates) {
              const storedRate = Number(li.tax_rate);
              const match = taxRates.find((r) => {
                const ratePercent = r.rate < 1 ? r.rate * 100 : r.rate;
                return Math.abs(ratePercent - storedRate) < 0.01;
              });
              if (match) resolvedTaxRateId = match.id;
            }
            return {
              source: 'manual' as const,
              description: (li.description || '') as string,
              quantity: Number(li.quantity || 1),
              unitPrice: Number(li.unit_price || li.unitPrice || 0),
              taxRateId: resolvedTaxRateId,
            };
          }),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, editType, existingAdvanceRaw, entityConfigs, taxRates]);

  // Populate form when in edit mode for CREDIT NOTES
  useEffect(() => {
    if (isEditMode && editType === 'credit_note' && existingCreditNoteRaw) {
      setTxType('credit_note');
      const raw = existingCreditNoteRaw as Record<string, unknown>;
      setClientId((raw.client_id || raw.clientId || '') as string);
      setTransactionDate(raw.created_at ? String(raw.created_at).split('T')[0] : '');
      setAmount(String(raw.amount || ''));
      setReason((raw.reason || '') as string);
      setInvoiceId((raw.invoice_id || raw.invoiceId || '') as string);
      setLinkedInvoiceNumber((raw.invoice_number || raw.invoiceNumber || '') as string);

      // Load practice entity — credit notes inherit from their linked invoice
      const peId = (raw.practice_entity_id || raw.practiceEntityId || '') as string;
      if (peId && entityConfigs) {
        const matchedConfig = entityConfigs.find(
          (c) => c.practiceEntityId === peId || c.practiceEntityCode === peId,
        );
        if (matchedConfig) {
          setPracticeEntityCode(matchedConfig.practiceEntityCode || matchedConfig.practiceEntityId);
        }
      } else if (!peId && entityConfigs && entityConfigs.length === 1) {
        // If only one entity exists, auto-select it
        setPracticeEntityCode(
          entityConfigs[0].practiceEntityCode || entityConfigs[0].practiceEntityId,
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, editType, existingCreditNoteRaw, entityConfigs]);

  // Populate form when in edit mode for WRITE-OFFS
  useEffect(() => {
    if (isEditMode && editType === 'write_off' && existingWriteOffRaw) {
      setTxType('write_off');
      const raw = existingWriteOffRaw as Record<string, unknown>;
      setClientId((raw.client_id || raw.clientId || '') as string);
      setTransactionDate(
        raw.transaction_date || raw.transactionDate
          ? String(raw.transaction_date || raw.transactionDate).split('T')[0]
          : raw.created_at
            ? String(raw.created_at).split('T')[0]
            : '',
      );
      setAmount(String(raw.amount || ''));
      setReason((raw.reason || '') as string);
      setInvoiceId((raw.invoice_id || raw.invoiceId || '') as string);
      setLinkedInvoiceNumber((raw.invoice_number || raw.invoiceNumber || '') as string);

      // Load practice entity
      const peId = (raw.practice_entity_id || raw.practiceEntityId || '') as string;
      if (entityConfigs) {
        if (peId) {
          const matchedConfig = entityConfigs.find(
            (c) => c.practiceEntityId === peId || c.practiceEntityCode === peId,
          );
          if (matchedConfig) {
            setPracticeEntityCode(
              matchedConfig.practiceEntityCode || matchedConfig.practiceEntityId,
            );
          }
        } else if (entityConfigs.length === 1) {
          setPracticeEntityCode(
            entityConfigs[0].practiceEntityCode || entityConfigs[0].practiceEntityId,
          );
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, editType, existingWriteOffRaw, entityConfigs]);

  // Populate form when in edit mode for DEBIT NOTES
  useEffect(() => {
    if (isEditMode && editType === 'debit_note' && existingDebitNoteRaw) {
      setTxType('debit_note');
      const raw = existingDebitNoteRaw as Record<string, unknown>;
      setClientId((raw.client_id || raw.clientId || '') as string);
      setTransactionDate(
        raw.transaction_date || raw.transactionDate
          ? String(raw.transaction_date || raw.transactionDate).split('T')[0]
          : raw.created_at
            ? String(raw.created_at).split('T')[0]
            : '',
      );
      setAmount(String(raw.amount || ''));
      setReason((raw.reason || '') as string);

      // Load practice entity
      const peId = (raw.practice_entity_id || raw.practiceEntityId || '') as string;
      if (entityConfigs) {
        if (peId) {
          const matchedConfig = entityConfigs.find(
            (c) => c.practiceEntityId === peId || c.practiceEntityCode === peId,
          );
          if (matchedConfig) {
            setPracticeEntityCode(
              matchedConfig.practiceEntityCode || matchedConfig.practiceEntityId,
            );
          }
        } else if (entityConfigs.length === 1) {
          setPracticeEntityCode(
            entityConfigs[0].practiceEntityCode || entityConfigs[0].practiceEntityId,
          );
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, editType, existingDebitNoteRaw, entityConfigs]);

  const isLineItemType = LINE_ITEM_TYPES.includes(txType);
  const needsInvoiceRef = INVOICE_REF_TYPES.includes(txType);
  const optionalInvoiceRef = OPTIONAL_INVOICE_TYPES.includes(txType);
  const needsReason = REASON_TYPES.includes(txType);
  const isQuote = txType === 'quote';
  const isAdvance = txType === 'advance';

  // Determine if form should be read-only (non-draft status in edit mode)
  const editStatus = isEditMode
    ? existingInvoice?.status ||
      ((existingQuoteRaw as Record<string, unknown>)?.status as string) ||
      ((existingAdvanceRaw as Record<string, unknown>)?.status as string) ||
      ((existingCreditNoteRaw as Record<string, unknown>)?.status as string) ||
      ((existingWriteOffRaw as Record<string, unknown>)?.status as string) ||
      ((existingDebitNoteRaw as Record<string, unknown>)?.status as string) ||
      ''
    : '';
  const isReadOnly = isEditMode && editStatus !== 'draft' && editStatus !== '';

  // ─── Totals (for line item types) ─────────────────────────────────────────
  const roundingPrecision = selectedEntityConfig?.roundingPrecision ?? 0;

  const totals = useMemo(() => {
    if (!isLineItemType) return null;
    let grossTotal = 0;
    let totalDiscount = 0;
    let taxTotal = 0;
    for (const item of lineItems) {
      const gross = item.quantity * item.unitPrice;
      grossTotal += gross;
      const disc =
        item.discountType === 'flat'
          ? item.discountValue || 0
          : item.discountType === 'percentage'
            ? gross * ((item.discountValue || 0) / 100)
            : 0;
      totalDiscount += disc;
      const net = Math.max(0, gross - disc);
      if (!isAdvance) {
        // Chosen rate, else the line's stored percentage (e.g. from a quote).
        taxTotal += net * (lineTaxPercent(item, taxRates) / 100);
      }
    }
    // Net Total = Gross Total - Discount + Tax
    let netTotal = grossTotal - totalDiscount + taxTotal;

    // Apply rounding (round up to nearest precision unit)
    let roundingAdjustment = 0;
    if (roundingPrecision > 0 && netTotal > 0) {
      const rounded = Math.ceil(netTotal / roundingPrecision) * roundingPrecision;
      roundingAdjustment = rounded - netTotal;
      netTotal = rounded;
    }

    return { grossTotal, totalDiscount, taxTotal, roundingAdjustment, netTotal };
  }, [lineItems, taxRates, isLineItemType, isAdvance, roundingPrecision]);

  // ─── Line item helpers ────────────────────────────────────────────────────
  const updateLineItem = (index: number, updates: Partial<TransactionLineItem>) => {
    setLineItems((prev) => prev.map((li, i) => (i === index ? { ...li, ...updates } : li)));
  };

  const addLineItem = (source: TransactionLineItem['source']) => {
    setLineItems((prev) => [
      ...prev,
      { ...emptyLineItem(), source, taxRateId: defaultTaxRateId || undefined },
    ]);
  };

  const removeLineItem = (index: number) => {
    setLineItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleLineItemSource = (
    index: number,
    source: TransactionLineItem['source'],
    value: string,
  ) => {
    if (source === 'service') {
      const svc = (serviceTypeLookups || []).find((s) => s.code === value);
      updateLineItem(index, { source, serviceTypeCode: value, description: svc?.label || '' });
    } else if (source === 'job') {
      const job = jobOptions.find((j: { value: string; title: string }) => j.value === value);
      updateLineItem(index, { source, jobId: value, description: job?.title || '' });
    }
  };

  // ─── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!practiceEntityCode) {
      toast({ variant: 'error', title: 'Select a practice entity' });
      return;
    }
    if (!clientId) {
      toast({ variant: 'error', title: 'Select a client' });
      return;
    }
    if (requireDueDate && !dueDate && txType === 'invoice') {
      toast({ variant: 'error', title: 'Due date is required for this entity' });
      return;
    }
    if (isQuote && !expiryDate) {
      toast({ variant: 'error', title: 'Expiry date is required for quotes' });
      return;
    }
    if (needsReason && !reason.trim()) {
      toast({
        variant: 'error',
        title: `Reason is required for ${TRANSACTION_TYPE_LABELS[txType].toLowerCase()} transactions`,
      });
      return;
    }
    // Every line item must have a description (the API rejects blank ones).
    // Surface it clearly instead of letting the server error out.
    if (isLineItemType) {
      const missingIdx = lineItems.findIndex((li) => !li.description || !li.description.trim());
      if (missingIdx !== -1) {
        setShowLineItemErrors(true);
        toast({
          variant: 'error',
          title: 'Description is required',
          description: `Enter a description for line item #${missingIdx + 1}${
            lineItems[missingIdx]?.source === 'service' ? ' (select a service)' : ''
          }.`,
        });
        return;
      }
    }

    const payload: BillingTransactionPayload = {
      transactionType: txType,
      practiceEntityCode: selectedEntityConfig?.practiceEntityId || practiceEntityCode,
      clientId,
      transactionDate,
      dueDate: dueDate || undefined,
      expiryDate: expiryDate || undefined,
      invoiceId: needsInvoiceRef || optionalInvoiceRef ? invoiceId || undefined : undefined,
      amount: amount ? parseFloat(amount) : undefined,
      discountType: txType === 'discount' ? discountType : undefined,
      reason: reason || undefined,
      notes: pendingNotes.length > 0 ? pendingNotes[0].content : undefined,
      serviceTypeCode: serviceTypeCode || undefined,
      jobId: linkedJobInfo?.id || undefined,
      jobReference: linkedJobInfo
        ? `${linkedJobInfo.jobCode || ''} ${linkedJobInfo.title}${linkedJobInfo.clientName ? ' | ' + linkedJobInfo.clientName : ''}`.trim()
        : undefined,
      discountDefinitionId: selectedDiscountId || undefined,
      assignedToId: assignedToId || undefined,
      lineItems: isLineItemType ? lineItems : undefined,
    };

    try {
      if (isEditMode && (editType === 'invoice' || txType === 'invoice')) {
        // Update existing invoice
        await updateInvoiceMutation.mutateAsync({
          clientId,
          billingEntityId: selectedEntityConfig?.practiceEntityId || practiceEntityCode,
          issueDate: transactionDate || undefined,
          dueDate: dueDate || undefined,
          notes: pendingNotes.length > 0 ? pendingNotes[0].content : undefined,
          discountDefinitionId: selectedDiscountId || undefined,
          lineItems: isLineItemType
            ? lineItems.map((li) => ({
                description: li.description,
                quantity: li.quantity,
                unitPrice: li.unitPrice,
                taxRate: 'exempt' as const,
                taxRateId: li.taxRateId || undefined,
                // Keep the stored percentage when no rate id is chosen, so a
                // rate carried over from a quote isn't reset to 0%.
                taxRatePercent: li.taxRateId ? undefined : li.taxRatePercent,
                discountType: li.discountType || undefined,
                discountValue: li.discountValue || undefined,
              }))
            : undefined,
        });
        toast({ variant: 'success', title: 'Invoice updated successfully' });
        navigate('/billing');
      } else {
        const result = (await createMutation.mutateAsync(payload)) as
          | Record<string, unknown>
          | undefined;

        // Save pending notes if transaction was created and we have the ID
        const createdId = result?.id as string | undefined;
        if (createdId && pendingNotes.length > 0) {
          for (const pNote of pendingNotes) {
            try {
              await apiClient.post(`/billing/invoices/${createdId}/notes`, {
                category: pNote.category || null,
                content: pNote.content,
                reminder_date: pNote.reminderDate || null,
                reminder_assignee_id: pNote.reminderAssigneeId || null,
                client_visible: pNote.clientVisible || false,
              });
            } catch {
              /* non-blocking — note save failure shouldn't block navigation */
            }
          }
        }

        toast({
          variant: 'success',
          title: `${TRANSACTION_TYPE_LABELS[txType]} created successfully`,
        });
        navigate('/billing');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save transaction';
      toast({ variant: 'error', title: msg });
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  if (isEditMode && editLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
        <span className="ml-3 text-gray-500">Loading transaction...</span>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {/* Header with action buttons */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {isEditMode
              ? `${TRANSACTION_TYPE_LABELS[txType]} Details`
              : `New ${TRANSACTION_TYPE_LABELS[txType]}`}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {isEditMode && (existingInvoice || existingQuoteRaw || existingAdvanceRaw) ? (
              <span className="flex items-center gap-2">
                {existingInvoice
                  ? existingInvoice.invoiceNumber
                  : existingQuoteRaw
                    ? (((existingQuoteRaw as Record<string, unknown>)?.quote_number ||
                        (existingQuoteRaw as Record<string, unknown>)?.quoteNumber ||
                        '') as string)
                    : (((existingAdvanceRaw as Record<string, unknown>)?.advance_number ||
                        '') as string)}
                {(() => {
                  const st = existingInvoice
                    ? existingInvoice.status
                    : existingQuoteRaw
                      ? (((existingQuoteRaw as Record<string, unknown>)?.status ||
                          'draft') as string)
                      : (((existingAdvanceRaw as Record<string, unknown>)?.status ||
                          'draft') as string);
                  return (
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                        st === 'paid' ||
                        st === 'accepted' ||
                        st === 'received' ||
                        st === 'fully_used'
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : st === 'sent' || st === 'partially_used'
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                            : st === 'void' || st === 'declined' || st === 'expired'
                              ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                              : st === 'overdue'
                                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {st.replace(/_/g, ' ')}
                    </span>
                  );
                })()}
              </span>
            ) : (
              'Create a billing transaction for a client'
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {/* Invoice lifecycle actions (edit mode) */}
          {isEditMode &&
            existingInvoice &&
            existingInvoice.status === 'draft' &&
            can(PERMISSIONS.BILLING_SEND_INVOICE) && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                loading={sendMutation.isPending}
                disabled={sendMutation.isPending}
                onClick={async () => {
                  try {
                    await sendMutation.mutateAsync(editId!);
                    toast({ variant: 'success', title: 'Invoice sent successfully' });
                    navigate('/billing');
                  } catch {
                    toast({ variant: 'error', title: 'Failed to send invoice' });
                  }
                }}
              >
                Send Invoice
              </Button>
            )}
          {isEditMode &&
            existingInvoice &&
            ['sent', 'viewed', 'partially_paid', 'overdue'].includes(existingInvoice.status) &&
            can(PERMISSIONS.BILLING_RECORD_PAYMENT) && (
              <Button type="button" size="sm" onClick={() => setShowPaymentModal(true)}>
                Record Payment
              </Button>
            )}
          {isEditMode &&
            existingInvoice &&
            ['sent', 'viewed', 'partially_paid', 'overdue', 'paid'].includes(
              existingInvoice.status,
            ) &&
            can(PERMISSIONS.DOCUMENTS_READ) && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                loading={printMutation.isPending}
                disabled={printMutation.isPending}
                onClick={async () => {
                  try {
                    const result = await printMutation.mutateAsync(editId!);
                    if (result.downloadUrl) {
                      window.open(result.downloadUrl, '_blank');
                      toast({ variant: 'success', title: 'Invoice PDF generated' });
                    }
                  } catch {
                    toast({
                      variant: 'error',
                      title:
                        'Failed to generate PDF. Ensure a default invoice template is configured.',
                    });
                  }
                }}
              >
                <svg
                  className="-ml-0.5 mr-1.5 h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0 1 10.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0 .229 2.523a1.125 1.125 0 0 1-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0 0 21 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 0 0-1.913-.247M6.34 18H5.25A2.25 2.25 0 0 1 3 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 0 1 1.913-.247m10.5 0a48.536 48.536 0 0 0-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18.75 7.131a4.137 4.137 0 0 0-.413-.091"
                  />
                </svg>
                Print Invoice
              </Button>
            )}
          {/* Print Quote */}
          {isEditMode &&
            editType === 'quote' &&
            existingQuoteRaw &&
            ['sent', 'accepted'].includes(
              (existingQuoteRaw as Record<string, unknown>).status as string,
            ) &&
            can(PERMISSIONS.DOCUMENTS_READ) && (
              <Button
                type="button"
                variant="outline"
                loading={printQuoteMutation.isPending}
                disabled={printQuoteMutation.isPending}
                onClick={async () => {
                  try {
                    const result = await printQuoteMutation.mutateAsync(editId!);
                    if (result.downloadUrl) {
                      window.open(result.downloadUrl, '_blank');
                      toast({ variant: 'success', title: 'Quote PDF generated' });
                    }
                  } catch {
                    toast({
                      variant: 'error',
                      title:
                        'Failed to generate PDF. Ensure a default quote template is configured.',
                    });
                  }
                }}
              >
                <svg
                  className="-ml-0.5 mr-1.5 h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0 1 10.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0 .229 2.523a1.125 1.125 0 0 1-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0 0 21 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 0 0-1.913-.247M6.34 18H5.25A2.25 2.25 0 0 1 3 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 0 1 1.913-.247m10.5 0a48.536 48.536 0 0 0-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18.75 7.131a4.137 4.137 0 0 0-.413-.091"
                  />
                </svg>
                Print Quote
              </Button>
            )}
          {/* Print Credit Note */}
          {isEditMode &&
            editType === 'credit_note' &&
            existingCreditNoteRaw &&
            (existingCreditNoteRaw as Record<string, unknown>).status === 'applied' &&
            can(PERMISSIONS.DOCUMENTS_READ) && (
              <Button
                type="button"
                variant="outline"
                loading={printCreditNoteMutation.isPending}
                disabled={printCreditNoteMutation.isPending}
                onClick={async () => {
                  try {
                    const result = await printCreditNoteMutation.mutateAsync(editId!);
                    if (result.downloadUrl) {
                      window.open(result.downloadUrl, '_blank');
                      toast({ variant: 'success', title: 'Credit Note PDF generated' });
                    }
                  } catch {
                    toast({
                      variant: 'error',
                      title:
                        'Failed to generate PDF. Ensure a default credit note template is configured.',
                    });
                  }
                }}
              >
                <svg
                  className="-ml-0.5 mr-1.5 h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0 1 10.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0 .229 2.523a1.125 1.125 0 0 1-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0 0 21 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 0 0-1.913-.247M6.34 18H5.25A2.25 2.25 0 0 1 3 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 0 1 1.913-.247m10.5 0a48.536 48.536 0 0 0-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18.75 7.131a4.137 4.137 0 0 0-.413-.091"
                  />
                </svg>
                Print Credit Note
              </Button>
            )}
          {/* Print Debit Note */}
          {isEditMode &&
            editType === 'debit_note' &&
            existingDebitNoteRaw &&
            (existingDebitNoteRaw as Record<string, unknown>).status === 'issued' &&
            can(PERMISSIONS.DOCUMENTS_READ) && (
              <Button
                type="button"
                variant="outline"
                loading={printDebitNoteMutation.isPending}
                disabled={printDebitNoteMutation.isPending}
                onClick={async () => {
                  try {
                    const result = await printDebitNoteMutation.mutateAsync(editId!);
                    if (result.downloadUrl) {
                      window.open(result.downloadUrl, '_blank');
                      toast({ variant: 'success', title: 'Debit Note PDF generated' });
                    }
                  } catch {
                    toast({
                      variant: 'error',
                      title:
                        'Failed to generate PDF. Ensure a default debit note template is configured.',
                    });
                  }
                }}
              >
                <svg
                  className="-ml-0.5 mr-1.5 h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0 1 10.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0 .229 2.523a1.125 1.125 0 0 1-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0 0 21 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 0 0-1.913-.247M6.34 18H5.25A2.25 2.25 0 0 1 3 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 0 1 1.913-.247m10.5 0a48.536 48.536 0 0 0-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18.75 7.131a4.137 4.137 0 0 0-.413-.091"
                  />
                </svg>
                Print Debit Note
              </Button>
            )}
          {/* Print Write-Off */}
          {isEditMode &&
            editType === 'write_off' &&
            existingWriteOffRaw &&
            (existingWriteOffRaw as Record<string, unknown>).status === 'applied' &&
            can(PERMISSIONS.DOCUMENTS_READ) && (
              <Button
                type="button"
                variant="outline"
                loading={printWriteOffMutation.isPending}
                disabled={printWriteOffMutation.isPending}
                onClick={async () => {
                  try {
                    const result = await printWriteOffMutation.mutateAsync(editId!);
                    if (result.downloadUrl) {
                      window.open(result.downloadUrl, '_blank');
                      toast({ variant: 'success', title: 'Write-Off PDF generated' });
                    }
                  } catch {
                    toast({
                      variant: 'error',
                      title:
                        'Failed to generate PDF. Ensure a default write-off template is configured.',
                    });
                  }
                }}
              >
                <svg
                  className="-ml-0.5 mr-1.5 h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0 1 10.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0 .229 2.523a1.125 1.125 0 0 1-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0 0 21 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 0 0-1.913-.247M6.34 18H5.25A2.25 2.25 0 0 1 3 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 0 1 1.913-.247m10.5 0a48.536 48.536 0 0 0-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18.75 7.131a4.137 4.137 0 0 0-.413-.091"
                  />
                </svg>
                Print Write-Off
              </Button>
            )}
          {/* Print Advance */}
          {isEditMode &&
            editType === 'advance' &&
            existingAdvanceRaw &&
            (existingAdvanceRaw as Record<string, unknown>).status === 'received' &&
            can(PERMISSIONS.DOCUMENTS_READ) && (
              <Button
                type="button"
                variant="outline"
                loading={printAdvanceMutation.isPending}
                disabled={printAdvanceMutation.isPending}
                onClick={async () => {
                  try {
                    const result = await printAdvanceMutation.mutateAsync(editId!);
                    if (result.downloadUrl) {
                      window.open(result.downloadUrl, '_blank');
                      toast({ variant: 'success', title: 'Advance Receipt PDF generated' });
                    }
                  } catch {
                    toast({
                      variant: 'error',
                      title:
                        'Failed to generate PDF. Ensure a default advance template is configured.',
                    });
                  }
                }}
              >
                <svg
                  className="-ml-0.5 mr-1.5 h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0 1 10.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0 .229 2.523a1.125 1.125 0 0 1-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0 0 21 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 0 0-1.913-.247M6.34 18H5.25A2.25 2.25 0 0 1 3 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 0 1 1.913-.247m10.5 0a48.536 48.536 0 0 0-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18.75 7.131a4.137 4.137 0 0 0-.413-.091"
                  />
                </svg>
                Print Advance
              </Button>
            )}
          {isEditMode &&
            existingInvoice &&
            ['sent', 'viewed', 'partially_paid', 'overdue'].includes(existingInvoice.status) &&
            can(PERMISSIONS.BILLING_CREATE_ADVANCE) && (
              <Button type="button" variant="outline" onClick={() => setShowAdvanceModal(true)}>
                Apply Advance
              </Button>
            )}
          {isEditMode &&
            existingInvoice &&
            existingInvoice.status !== 'void' &&
            existingInvoice.status !== 'paid' &&
            can(PERMISSIONS.BILLING_VOID_INVOICE) && (
              <Button
                type="button"
                variant="danger"
                size="sm"
                onClick={() => setShowVoidModal(true)}
              >
                Void
              </Button>
            )}
          {/* Quote lifecycle actions (edit mode) */}
          {isEditMode &&
            editType === 'quote' &&
            existingQuoteRaw &&
            (existingQuoteRaw as Record<string, unknown>).status === 'draft' &&
            can(PERMISSIONS.BILLING_MANAGE_QUOTES) && (
              <Button
                type="button"
                variant="outline"
                disabled={sendingQuote}
                loading={sendingQuote}
                onClick={async () => {
                  setSendingQuote(true);
                  try {
                    await apiClient.patch(`/billing/quotes/${editId}/status`, { status: 'sent' });
                    queryClient.invalidateQueries({ queryKey: ['billing', 'quotes'] });
                    toast({ variant: 'success', title: 'Quote sent successfully' });
                    navigate('/billing');
                  } catch {
                    toast({ variant: 'error', title: 'Failed to send quote' });
                    setSendingQuote(false);
                  }
                }}
              >
                Send Quote
              </Button>
            )}
          {isEditMode &&
            editType === 'quote' &&
            existingQuoteRaw &&
            (existingQuoteRaw as Record<string, unknown>).status === 'sent' &&
            can(PERMISSIONS.BILLING_MANAGE_QUOTES) && (
              <>
                <Button
                  type="button"
                  disabled={acceptingQuote}
                  loading={acceptingQuote}
                  onClick={async () => {
                    setAcceptingQuote(true);
                    try {
                      await apiClient.post(`/billing/quotes/${editId}/accept`);
                      queryClient.invalidateQueries({ queryKey: ['billing', 'quotes'] });
                      toast({ variant: 'success', title: 'Quote accepted' });
                      navigate('/billing');
                    } catch (err: unknown) {
                      const error = err as {
                        response?: { data?: { error?: { message?: string } } };
                      };
                      toast({
                        variant: 'error',
                        title: error?.response?.data?.error?.message || 'Failed to accept quote',
                      });
                      setAcceptingQuote(false);
                    }
                  }}
                >
                  Accept
                </Button>
                <Button
                  type="button"
                  variant="danger"
                  disabled={decliningQuote}
                  loading={decliningQuote}
                  onClick={async () => {
                    setDecliningQuote(true);
                    try {
                      await apiClient.patch(`/billing/quotes/${editId}/status`, {
                        status: 'declined',
                      });
                      queryClient.invalidateQueries({ queryKey: ['billing', 'quotes'] });
                      toast({ variant: 'success', title: 'Quote declined' });
                      navigate('/billing');
                    } catch {
                      toast({ variant: 'error', title: 'Failed to decline quote' });
                      setDecliningQuote(false);
                    }
                  }}
                >
                  Decline
                </Button>
              </>
            )}
          {isEditMode &&
            editType === 'quote' &&
            existingQuoteRaw &&
            (existingQuoteRaw as Record<string, unknown>).status === 'accepted' &&
            can(PERMISSIONS.BILLING_MANAGE_QUOTES) && (
              <Button
                type="button"
                disabled={convertingQuote}
                loading={convertingQuote}
                onClick={async () => {
                  setConvertingQuote(true);
                  try {
                    await apiClient.post(`/billing/quotes/${editId}/convert`);
                    toast({ variant: 'success', title: 'Invoice created from quote' });
                    navigate('/billing');
                  } catch (err: unknown) {
                    const error = err as { response?: { data?: { error?: { message?: string } } } };
                    toast({
                      variant: 'error',
                      title:
                        error?.response?.data?.error?.message ||
                        'Failed to convert quote to invoice',
                    });
                    setConvertingQuote(false);
                  }
                }}
              >
                Convert to Invoice
              </Button>
            )}
          {/* Void quote (available for draft and sent) */}
          {isEditMode &&
            editType === 'quote' &&
            existingQuoteRaw &&
            ['draft', 'sent'].includes(
              (existingQuoteRaw as Record<string, unknown>).status as string,
            ) &&
            can(PERMISSIONS.BILLING_MANAGE_QUOTES) && (
              <Button
                type="button"
                variant="danger"
                disabled={voidingQuote}
                loading={voidingQuote}
                onClick={async () => {
                  setVoidingQuote(true);
                  try {
                    await apiClient.patch(`/billing/quotes/${editId}/status`, { status: 'voided' });
                    queryClient.invalidateQueries({ queryKey: ['billing', 'quotes'] });
                    toast({ variant: 'success', title: 'Quote voided' });
                    navigate('/billing');
                  } catch {
                    toast({ variant: 'error', title: 'Failed to void quote' });
                    setVoidingQuote(false);
                  }
                }}
              >
                Void
              </Button>
            )}
          {/* Advance lifecycle buttons */}
          {isEditMode &&
            editType === 'advance' &&
            existingAdvanceRaw &&
            (existingAdvanceRaw as Record<string, unknown>).status === 'draft' &&
            can(PERMISSIONS.BILLING_CREATE_ADVANCE) && (
              <Button
                type="button"
                onClick={async () => {
                  try {
                    await apiClient.patch(`/billing/advances/${editId}/status`, {
                      status: 'received',
                    });
                    queryClient.invalidateQueries({ queryKey: ['billing'] });
                    queryClient.invalidateQueries({ queryKey: ['billing', 'advances'] });
                    toast({ variant: 'success', title: 'Advance marked as received' });
                    navigate('/billing');
                  } catch {
                    toast({ variant: 'error', title: 'Failed to mark advance as received' });
                  }
                }}
              >
                Mark Received
              </Button>
            )}
          {isEditMode &&
            editType === 'advance' &&
            existingAdvanceRaw &&
            ['draft', 'received'].includes(
              (existingAdvanceRaw as Record<string, unknown>).status as string,
            ) &&
            can(PERMISSIONS.BILLING_CREATE_ADVANCE) && (
              <Button
                type="button"
                variant="danger"
                onClick={async () => {
                  try {
                    await apiClient.patch(`/billing/advances/${editId}/status`, {
                      status: 'cancelled',
                    });
                    toast({ variant: 'success', title: 'Advance cancelled' });
                    navigate('/billing');
                  } catch {
                    toast({ variant: 'error', title: 'Failed to cancel advance' });
                  }
                }}
              >
                Cancel
              </Button>
            )}
          {/* Void buttons for write-offs, credit notes, discounts, debit notes */}
          {isEditMode &&
            editType === 'write_off' &&
            existingWriteOffRaw &&
            (existingWriteOffRaw as Record<string, unknown>).status === 'applied' &&
            can(PERMISSIONS.BILLING_VOID_INVOICE) && (
              <Button type="button" variant="danger" onClick={() => setShowVoidModal(true)}>
                Void Write-Off
              </Button>
            )}
          {isEditMode &&
            editType === 'credit_note' &&
            existingCreditNoteRaw &&
            (existingCreditNoteRaw as Record<string, unknown>).status === 'applied' &&
            can(PERMISSIONS.BILLING_VOID_INVOICE) && (
              <Button type="button" variant="danger" onClick={() => setShowVoidModal(true)}>
                Void Credit Note
              </Button>
            )}
          {isEditMode &&
            editType === 'discount' &&
            existingDiscountRaw &&
            (existingDiscountRaw as Record<string, unknown>).status === 'applied' &&
            can(PERMISSIONS.BILLING_VOID_INVOICE) && (
              <Button type="button" variant="danger" onClick={() => setShowVoidModal(true)}>
                Void Discount
              </Button>
            )}
          {isEditMode &&
            editType === 'debit_note' &&
            existingDebitNoteRaw &&
            (existingDebitNoteRaw as Record<string, unknown>).status === 'draft' &&
            can(PERMISSIONS.BILLING_CREATE_DEBIT_NOTE) && (
              <>
                <Button
                  type="button"
                  onClick={async () => {
                    try {
                      await apiClient.patch(`/billing/debit-notes/${editId}/status`, {
                        status: 'issued',
                      });
                      queryClient.invalidateQueries({ queryKey: ['billing'] });
                      queryClient.invalidateQueries({ queryKey: ['debit-notes'] });
                      toast({ variant: 'success', title: 'Debit note accepted and issued' });
                      navigate('/billing');
                    } catch {
                      toast({ variant: 'error', title: 'Failed to accept debit note' });
                    }
                  }}
                >
                  Accept
                </Button>
                <Button
                  type="button"
                  variant="danger"
                  onClick={async () => {
                    try {
                      await apiClient.patch(`/billing/debit-notes/${editId}/status`, {
                        status: 'cancelled',
                      });
                      queryClient.invalidateQueries({ queryKey: ['billing'] });
                      queryClient.invalidateQueries({ queryKey: ['debit-notes'] });
                      toast({ variant: 'success', title: 'Debit note cancelled' });
                      navigate('/billing');
                    } catch {
                      toast({ variant: 'error', title: 'Failed to cancel debit note' });
                    }
                  }}
                >
                  Cancel
                </Button>
              </>
            )}
          {isEditMode &&
            editType === 'debit_note' &&
            existingDebitNoteRaw &&
            (existingDebitNoteRaw as Record<string, unknown>).status === 'issued' &&
            can(PERMISSIONS.BILLING_VOID_INVOICE) && (
              <>
                <Button
                  type="button"
                  variant="danger"
                  onClick={async () => {
                    try {
                      await apiClient.patch(`/billing/debit-notes/${editId}/status`, {
                        status: 'cancelled',
                      });
                      queryClient.invalidateQueries({ queryKey: ['billing'] });
                      queryClient.invalidateQueries({ queryKey: ['debit-notes'] });
                      toast({ variant: 'success', title: 'Debit note cancelled and reversed' });
                      navigate('/billing');
                    } catch {
                      toast({ variant: 'error', title: 'Failed to cancel debit note' });
                    }
                  }}
                >
                  Cancel
                </Button>
              </>
            )}
          {/* Debit Note: Void / Cancel */}
          {(!isEditMode ||
            (isEditMode && existingInvoice?.status === 'draft') ||
            (isEditMode &&
              editType === 'quote' &&
              existingQuoteRaw &&
              (existingQuoteRaw as Record<string, unknown>).status === 'draft') ||
            (isEditMode &&
              editType === 'advance' &&
              existingAdvanceRaw &&
              (existingAdvanceRaw as Record<string, unknown>).status === 'draft')) && (
            <Button form="billing-tx-form" type="submit" loading={createMutation.isPending}>
              {isEditMode
                ? `Update ${TRANSACTION_TYPE_LABELS[txType]}`
                : `Create ${TRANSACTION_TYPE_LABELS[txType]}`}
            </Button>
          )}
          {/* Bill the client's whole group as one consolidated invoice.
              Only when creating a NEW invoice — not while viewing/editing an
              existing (draft or otherwise) invoice. */}
          {!isEditMode &&
            txType === 'invoice' &&
            clientId &&
            can(PERMISSIONS.GROUPS_MANAGE_INVOICING) && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowGroupInvoice(true)}
              >
                Bill Entire Group
              </Button>
            )}
          {/* Close is always the last action in the row. */}
          <Button type="button" variant="outline" size="sm" onClick={() => navigate(-1)}>
            Close
          </Button>
        </div>
      </div>

      <form id="billing-tx-form" onSubmit={handleSubmit} noValidate className="space-y-6">
        <fieldset disabled={isReadOnly} className="space-y-6 disabled:opacity-75">
          {/* Step 1: Transaction type + practice entity + client */}
          <Card>
            <CardHeader title="Transaction Details" />
            <CardContent>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <Select
                      label="Transaction Type"
                      value={txType}
                      onChange={(e) => setTxType(e.target.value as BillingTransactionType)}
                      disabled={isEditMode}
                      options={TRANSACTION_TYPES.filter((t) => {
                        switch (t) {
                          case 'invoice':
                            return can(PERMISSIONS.BILLING_CREATE_INVOICE);
                          case 'quote':
                            return can(PERMISSIONS.BILLING_MANAGE_QUOTES);
                          case 'credit_note':
                            return can(PERMISSIONS.BILLING_CREATE_CREDIT_NOTE);
                          case 'debit_note':
                            return can(PERMISSIONS.BILLING_CREATE_DEBIT_NOTE);
                          case 'write_off':
                            return can(PERMISSIONS.BILLING_CREATE_WRITE_OFF);
                          case 'discount':
                            return can(PERMISSIONS.BILLING_CREATE_DISCOUNT);
                          case 'advance':
                            return can(PERMISSIONS.BILLING_CREATE_ADVANCE);
                          default:
                            return false;
                        }
                      }).map((t) => ({
                        value: t,
                        label: TRANSACTION_TYPE_LABELS[t],
                      }))}
                    />
                  </div>
                  {isLineItemType && !isAdvance && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowAddressModal(true)}
                      title="Billing & Postal Address"
                      className="h-[38px] px-3"
                    >
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    </Button>
                  )}
                </div>
                {/* Partner — the staff member this transaction is assigned to.
                    Defaults to the current user; reassign via the staff search
                    popup (filter by status / job role). */}
                <div>
                  <label
                    htmlFor="billing-partner-search"
                    className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Partner
                  </label>
                  <button
                    id="billing-partner-search"
                    type="button"
                    onClick={() => setShowStaffSearchModal(true)}
                    disabled={isReadOnly}
                    title="The staff member this transaction is assigned to"
                    className="flex h-[38px] w-full items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white px-3 text-left text-sm shadow-sm hover:border-brand-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  >
                    <span className={assignedToName ? '' : 'text-gray-400'}>
                      {assignedToName || 'Search & select partner...'}
                    </span>
                    <svg
                      className="h-4 w-4 shrink-0 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
                {/* Client — mandatory, selected via searchable popup (same as
                    jobs / document sharing). Shown before Practice Entity so the
                    customer is chosen first (and can auto-fill the entity). */}
                <div>
                  <label
                    htmlFor="billing-client-search"
                    className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Client <span className="text-red-500">*</span>
                  </label>
                  <button
                    id="billing-client-search"
                    type="button"
                    onClick={() => setShowClientSearchModal(true)}
                    disabled={isReadOnly}
                    aria-required="true"
                    className="flex h-[38px] w-full items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white px-3 text-left text-sm shadow-sm hover:border-brand-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  >
                    <span className={clientName ? '' : 'text-gray-400'}>
                      {clientName || 'Search & select client...'}
                    </span>
                    <svg
                      className="h-4 w-4 shrink-0 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </button>
                </div>
                <Select
                  label="Practice Entity *"
                  value={practiceEntityCode}
                  onChange={(e) => setPracticeEntityCode(e.target.value)}
                  options={entityOptions}
                  placeholder="Select entity..."
                  required
                />
                <Input
                  label="Transaction Date"
                  type="date"
                  value={transactionDate}
                  onChange={(e) => setTransactionDate(e.target.value)}
                  required
                />
              </div>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
                {/* Dates */}
                {txType === 'invoice' && (
                  <Input
                    label={`Due Date${requireDueDate ? ' *' : ''}`}
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    hint={
                      selectedEntityConfig
                        ? `${selectedEntityConfig.defaultPaymentTermsDays ?? 30}-day terms`
                        : undefined
                    }
                  />
                )}
                {isQuote && (
                  <Input
                    label="Expiry Date"
                    type="date"
                    value={expiryDate}
                    onChange={(e) => setExpiryDate(e.target.value)}
                  />
                )}
                {/* Job Reference (for invoice/quote) — opens search modal */}
                {isLineItemType && (
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Job Reference
                    </label>
                    {linkedJobInfo ? (
                      <div className="flex items-center gap-2 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 dark:border-brand-700 dark:bg-brand-900/20">
                        <span className="flex-1 text-sm font-medium text-brand-700 dark:text-brand-300 truncate">
                          {linkedJobInfo.jobCode && (
                            <span className="mr-1.5 rounded bg-brand-100 px-1.5 py-0.5 font-mono text-xs dark:bg-brand-800">
                              {linkedJobInfo.jobCode}
                            </span>
                          )}
                          {linkedJobInfo.title}
                          {linkedJobInfo.clientName && (
                            <span className="ml-1.5 text-xs text-gray-500 font-normal">
                              ({linkedJobInfo.clientName})
                            </span>
                          )}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            setLinkedJobInfo(null);
                            setLineItems([emptyLineItem()]);
                          }}
                          className="shrink-0 text-xs text-red-500 hover:underline"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowJobSearchModal(true)}
                        className="w-full justify-start text-left"
                      >
                        <svg
                          className="mr-2 h-4 w-4 text-gray-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                        Select job reference...
                      </Button>
                    )}
                  </div>
                )}
                {/* Discount Category (for invoice/quote) — auto-applies or enables manual editing */}
                {isLineItemType &&
                  !isAdvance &&
                  (isAdmin || can(PERMISSIONS.BILLING_CREATE_DISCOUNT)) && (
                    <div>
                      <Select
                        label="Discount Category"
                        value={selectedDiscountId}
                        onChange={(e) => {
                          const discId = e.target.value;
                          setSelectedDiscountId(discId);
                          if (!discId) {
                            // Clear discounts from all line items
                            setLineItems((prev) =>
                              prev.map((li) => ({
                                ...li,
                                discountType: undefined,
                                discountValue: undefined,
                              })),
                            );
                            return;
                          }
                          const disc = (discountDefinitions || []).find((d) => d.id === discId);
                          if (!disc) return;
                          const meta = disc.metadata as {
                            discount_type?: string;
                            discount_value?: number;
                            discount_mode?: string;
                            service_rules?: Array<{
                              service_type: string;
                              discount_type: string;
                              discount_value: number;
                            }>;
                          };
                          // Manual mode — just enable line-item editing, don't auto-apply
                          if (meta.discount_mode === 'manual') {
                            return;
                          }
                          // Apply discount to line items based on service type rules
                          setLineItems((prev) =>
                            prev.map((li) => {
                              const rules = meta.service_rules ?? [];
                              if (rules.length > 0) {
                                let matchedRule = rules.find(
                                  (r) => r.service_type && r.service_type === li.serviceTypeCode,
                                );
                                if (!matchedRule && li.description) {
                                  const matchedSvc = (serviceTypeLookups || []).find(
                                    (s) =>
                                      s.label.toLowerCase() === li.description.toLowerCase() ||
                                      s.code === li.description,
                                  );
                                  if (matchedSvc) {
                                    matchedRule = rules.find(
                                      (r) => r.service_type === matchedSvc.code,
                                    );
                                  }
                                }
                                if (matchedRule) {
                                  return {
                                    ...li,
                                    discountType:
                                      matchedRule.discount_type === 'percentage'
                                        ? 'percentage'
                                        : 'flat',
                                    discountValue: matchedRule.discount_value,
                                  };
                                }
                                return {
                                  ...li,
                                  discountType: undefined,
                                  discountValue: undefined,
                                };
                              }
                              // Standard discount — apply to ALL line items
                              if (meta.discount_value && meta.discount_value > 0) {
                                return {
                                  ...li,
                                  discountType:
                                    meta.discount_type === 'percentage' ? 'percentage' : 'flat',
                                  discountValue: meta.discount_value,
                                };
                              }
                              return li;
                            }),
                          );
                        }}
                        options={[
                          { value: '', label: 'No discount' },
                          ...(discountDefinitions || [])
                            .filter((d) => {
                              if (!d.isActive) return false;
                              // Filter by client's contact category
                              const categories = (d.metadata?.contact_categories as string[]) ?? [];
                              if (categories.length === 0) return true; // No restriction — show for all
                              const selectedClient = (clientsData?.contacts ?? []).find(
                                (c) => c.id === clientId,
                              );
                              const clientCategory = selectedClient?.contactCategory;
                              if (!clientCategory) return true; // Client has no category — show all
                              return categories.includes(clientCategory);
                            })
                            .map((d) => ({ value: d.id, label: d.label })),
                        ]}
                      />
                    </div>
                  )}
              </div>
            </CardContent>
          </Card>

          {/* Invoice reference (credit note, write-off, optional discount) */}
          {(needsInvoiceRef || optionalInvoiceRef) && (
            <Card>
              <CardHeader
                title="Invoice Reference"
                description={
                  needsInvoiceRef
                    ? 'Select the invoice this transaction applies to.'
                    : 'Optionally link to a specific invoice.'
                }
              />
              <CardContent>
                <Select
                  label={needsInvoiceRef ? 'Invoice *' : 'Invoice (optional)'}
                  value={invoiceId}
                  onChange={(e) => setInvoiceId(e.target.value)}
                  options={
                    clientId
                      ? invoiceId && !invoiceOptions.some((o) => o.value === invoiceId)
                        ? [
                            { value: invoiceId, label: linkedInvoiceNumber || invoiceId },
                            ...invoiceOptions,
                          ]
                        : invoiceOptions
                      : invoiceId
                        ? [{ value: invoiceId, label: linkedInvoiceNumber || invoiceId }]
                        : []
                  }
                  placeholder={clientId ? 'Select invoice...' : 'Select client first'}
                  disabled={!clientId}
                />
              </CardContent>
            </Card>
          )}

          {/* Amount (for single-amount types) */}
          {!isLineItemType && (
            <Card>
              <CardHeader title="Amount" />
              <CardContent>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  {txType === 'discount' && (
                    <Select
                      label="Discount Type"
                      value={discountType}
                      onChange={(e) => setDiscountType(e.target.value as 'flat' | 'percentage')}
                      options={[
                        { value: 'flat', label: `Flat Amount (${currencySymbol})` },
                        { value: 'percentage', label: 'Percentage (%)' },
                      ]}
                    />
                  )}
                  <Input
                    label={
                      txType === 'discount' && discountType === 'percentage'
                        ? 'Percentage (%)'
                        : `Amount *`
                    }
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    required
                  />
                  <Select
                    label="Service Type (optional)"
                    value={serviceTypeCode}
                    onChange={(e) => setServiceTypeCode(e.target.value)}
                    options={serviceTypeOptions}
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Line items (invoice / quote) */}
          {isLineItemType && (
            <Card>
              <CardHeader
                title="Line Items"
                description="Add items from service types, client jobs, or manual entry"
              />
              <CardContent>
                <div className="space-y-3 text-xs">
                  {/* Header */}
                  <div
                    className="hidden gap-2 text-xs font-medium uppercase text-gray-500 dark:text-gray-400 sm:grid px-1"
                    style={{ gridTemplateColumns: '2fr 4fr 1.5fr 2fr 2fr 1.5fr 2fr 2fr 0.5fr' }}
                  >
                    <div className="px-2">Source</div>
                    <div className="px-2">
                      Description <span className="text-red-500">*</span>
                    </div>
                    <div className="px-2">
                      {selectedEntityConfig?.quantityLabelMode === 'quantity' ? 'Qty' : 'Hrs'}
                    </div>
                    <div className="px-2">Charges</div>
                    <div className="px-2">Disc. Type</div>
                    <div className="px-2">Disc. Value</div>
                    <div className="px-2">Tax</div>
                    <div className="px-2 text-right">Amount</div>
                    <div />
                  </div>

                  {/* Rows */}
                  {lineItems.map((item, idx) => {
                    const gross = item.quantity * item.unitPrice;
                    const disc =
                      item.discountType === 'flat'
                        ? item.discountValue || 0
                        : item.discountType === 'percentage'
                          ? gross * ((item.discountValue || 0) / 100)
                          : 0;
                    const net = Math.max(0, gross - disc);
                    // Calculate tax for this line item
                    const lineTax = isAdvance ? 0 : net * (lineTaxPercent(item, taxRates) / 100);
                    const lineTotal = net + lineTax;
                    return (
                      <div
                        key={idx}
                        className="grid grid-cols-1 gap-2 rounded-lg border border-gray-200 p-3 dark:border-gray-700 sm:border-0 sm:p-0"
                        style={{ gridTemplateColumns: '2fr 4fr 1.5fr 2fr 2fr 1.5fr 2fr 2fr 0.5fr' }}
                      >
                        <div>
                          <Select
                            value={item.source}
                            onChange={(e) => {
                              const src = e.target.value as TransactionLineItem['source'];
                              updateLineItem(idx, { source: src });
                            }}
                            options={[
                              { value: 'manual', label: 'Manual' },
                              { value: 'service', label: 'Service' },
                            ]}
                          />
                        </div>
                        <div>
                          {(() => {
                            const descMissing =
                              showLineItemErrors && (!item.description || !item.description.trim());
                            const descError = descMissing
                              ? item.source === 'service'
                                ? 'Select a service'
                                : 'Description is required'
                              : undefined;
                            return item.source === 'service' ? (
                              <Select
                                value={item.serviceTypeCode || ''}
                                onChange={(e) =>
                                  handleLineItemSource(idx, 'service', e.target.value)
                                }
                                options={serviceTypeOptions.filter((o) => o.value !== '')}
                                placeholder="Select service..."
                                error={descError}
                              />
                            ) : (
                              <Input
                                value={item.description}
                                onChange={(e) =>
                                  updateLineItem(idx, { description: e.target.value })
                                }
                                placeholder="Description"
                                required
                                error={descError}
                              />
                            );
                          })()}
                        </div>
                        <div>
                          <Input
                            type="number"
                            min={0.01}
                            step="0.01"
                            value={String(item.quantity)}
                            onChange={(e) =>
                              updateLineItem(idx, { quantity: parseFloat(e.target.value) || 0 })
                            }
                            placeholder="1"
                          />
                        </div>
                        <div>
                          <Input
                            type="number"
                            min={0}
                            step="0.01"
                            value={String(item.unitPrice)}
                            onChange={(e) =>
                              updateLineItem(idx, { unitPrice: parseFloat(e.target.value) || 0 })
                            }
                            placeholder="0.00"
                          />
                        </div>
                        <div>
                          <Select
                            value={item.discountType || ''}
                            onChange={(e) =>
                              updateLineItem(idx, {
                                discountType:
                                  (e.target.value as 'flat' | 'percentage') || undefined,
                                discountValue: e.target.value ? item.discountValue || 0 : undefined,
                              })
                            }
                            options={[
                              { value: '', label: 'None' },
                              { value: 'flat', label: 'Flat' },
                              { value: 'percentage', label: '%' },
                            ]}
                            className="text-xs"
                            disabled={(() => {
                              if (!isAdmin && !can(PERMISSIONS.BILLING_CREATE_DISCOUNT))
                                return true;
                              if (!selectedDiscountId) return true;
                              const disc = (discountDefinitions || []).find(
                                (d) => d.id === selectedDiscountId,
                              );
                              const meta = disc?.metadata as { discount_mode?: string } | undefined;
                              return meta?.discount_mode !== 'manual';
                            })()}
                          />
                        </div>
                        <div>
                          <Input
                            type="number"
                            min={0}
                            step="0.01"
                            value={item.discountValue ? String(item.discountValue) : ''}
                            onChange={(e) =>
                              updateLineItem(idx, {
                                discountValue: parseFloat(e.target.value) || 0,
                              })
                            }
                            placeholder="0"
                            disabled={(() => {
                              if (!isAdmin && !can(PERMISSIONS.BILLING_CREATE_DISCOUNT))
                                return true;
                              if (!selectedDiscountId) return true;
                              const disc = (discountDefinitions || []).find(
                                (d) => d.id === selectedDiscountId,
                              );
                              const meta = disc?.metadata as { discount_mode?: string } | undefined;
                              if (meta?.discount_mode !== 'manual') return true;
                              return !item.discountType;
                            })()}
                          />
                        </div>
                        <div>
                          <Select
                            value={isAdvance ? '' : item.taxRateId || ''}
                            onChange={(e) =>
                              // An explicit choice (including "no tax") replaces any
                              // stored percentage carried over from a quote.
                              updateLineItem(idx, {
                                taxRateId: e.target.value || undefined,
                                taxRatePercent: undefined,
                              })
                            }
                            options={taxRateOptions}
                            disabled={isAdvance}
                          />
                        </div>
                        <div className="flex items-center justify-end text-sm font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                          {currencySymbol}{' '}
                          {lineTotal.toLocaleString('en', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </div>
                        <div className="flex items-center justify-center">
                          {lineItems.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeLineItem(idx)}
                              className="text-red-500 hover:text-red-700 text-lg leading-none"
                            >
                              &times;
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* Add buttons */}
                  <div className="flex gap-2 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => addLineItem('manual')}
                    >
                      + Manual
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => addLineItem('service')}
                    >
                      + Service Type
                    </Button>
                  </div>
                </div>

                {/* Totals */}
                {totals && (
                  <div className="mt-4 border-t border-border pt-4 dark:border-border-dark">
                    <div className="ml-auto w-64 space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Gross Total</span>
                        <span className="font-medium">
                          {currencySymbol}{' '}
                          {totals.grossTotal.toLocaleString('en', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                      {totals.totalDiscount > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Discount</span>
                          <span className="font-medium text-green-600 dark:text-green-400">
                            -{currencySymbol}{' '}
                            {totals.totalDiscount.toLocaleString('en', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-500">Total Tax</span>
                        <span className="font-medium">
                          {currencySymbol}{' '}
                          {totals.taxTotal.toLocaleString('en', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                      {totals.roundingAdjustment > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Rounding</span>
                          <span className="font-medium">
                            +{currencySymbol}{' '}
                            {totals.roundingAdjustment.toLocaleString('en', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between border-t border-border pt-1 text-base font-bold dark:border-border-dark">
                        <span>Net Total</span>
                        <span>
                          {currencySymbol}{' '}
                          {totals.netTotal.toLocaleString('en', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Reason (for write-off, credit note, debit note, discount) */}
          {needsReason && (
            <Card>
              <CardHeader
                title={`Reason${needsInvoiceRef || txType === 'debit_note' ? ' *' : ''}`}
              />
              <CardContent>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={`Reason for ${TRANSACTION_TYPE_LABELS[txType].toLowerCase()}...`}
                  rows={3}
                  required
                  className="block w-full rounded-lg border border-border bg-white px-3 py-2 text-sm dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
                />
                {!reason && (
                  <p className="mt-1 text-xs text-gray-400">
                    A reason is required for {TRANSACTION_TYPE_LABELS[txType].toLowerCase()}{' '}
                    transactions.
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Balance Breakdown moved outside fieldset — see below */}
        </fieldset>

        {/* Balance Breakdown (invoice edit mode only) — outside fieldset so buttons are clickable */}
        {isEditMode && editType === 'invoice' && existingInvoice && (
          <Card>
            <CardHeader
              title="Balance Breakdown"
              action={
                <div className="flex items-center gap-2">
                  {existingInvoice.isBadDebt && (
                    <span className="inline-flex rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/30 dark:text-red-400">
                      Bad Debt
                    </span>
                  )}
                  {can(PERMISSIONS.BILLING_MARK_BAD_DEBT) &&
                    (existingInvoice.isBadDebt ||
                      (['sent', 'viewed', 'partially_paid', 'overdue'].includes(
                        existingInvoice.status,
                      ) &&
                        existingInvoice.outstandingBalance > 0)) && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        loading={badDebtMutation.isPending}
                        onClick={async () => {
                          const next = !existingInvoice.isBadDebt;
                          try {
                            await badDebtMutation.mutateAsync({
                              invoiceId: editId!,
                              isBadDebt: next,
                            });
                            toast({
                              variant: 'success',
                              title: next ? 'Invoice marked as bad debt' : 'Bad debt flag removed',
                            });
                          } catch (err) {
                            const msg =
                              err instanceof Error ? err.message : 'Failed to update bad debt flag';
                            toast({ variant: 'error', title: msg });
                          }
                        }}
                      >
                        {existingInvoice.isBadDebt ? 'Unmark Bad Debt' : 'Mark as Bad Debt'}
                      </Button>
                    )}
                </div>
              }
            />
            <CardContent>
              <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800/50 space-y-2">
                {/* Invoice Total */}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Invoice Total</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {currencySymbol}{' '}
                    {Number(existingInvoice.total).toLocaleString('en', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>

                {/* Credit Notes */}
                {invoiceCreditNotes &&
                  invoiceCreditNotes.filter((cn) => cn.status !== 'voided').length > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-blue-600 dark:text-blue-400">
                        Credit Notes (
                        {invoiceCreditNotes.filter((cn) => cn.status !== 'voided').length})
                      </span>
                      <span className="font-medium text-blue-600 dark:text-blue-400">
                        -{currencySymbol}{' '}
                        {invoiceCreditNotes
                          .filter((cn) => cn.status !== 'voided')
                          .reduce((sum, cn) => sum + cn.amount, 0)
                          .toLocaleString('en', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                      </span>
                    </div>
                  )}

                {/* Write-Offs */}
                {invoiceWriteOffs &&
                  invoiceWriteOffs.filter((wo) => wo.status !== 'voided').length > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-orange-600 dark:text-orange-400">
                        Write-Offs ({invoiceWriteOffs.filter((wo) => wo.status !== 'voided').length}
                        )
                      </span>
                      <span className="font-medium text-orange-600 dark:text-orange-400">
                        -{currencySymbol}{' '}
                        {invoiceWriteOffs
                          .filter((wo) => wo.status !== 'voided')
                          .reduce((sum: number, wo: { amount: number }) => sum + wo.amount, 0)
                          .toLocaleString('en', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                      </span>
                    </div>
                  )}

                {/* Advances Applied */}
                {appliedAdvances && appliedAdvances.length > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-green-600 dark:text-green-400">
                      Advances Applied ({appliedAdvances.length})
                    </span>
                    <span className="font-medium text-green-600 dark:text-green-400">
                      -{currencySymbol}{' '}
                      {appliedAdvances
                        .reduce((sum, a) => sum + Number(a.amount), 0)
                        .toLocaleString('en', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                    </span>
                  </div>
                )}

                {/* Payments */}
                {existingInvoice.payments && existingInvoice.payments.length > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-purple-600 dark:text-purple-400">
                      Payments ({existingInvoice.payments.length})
                    </span>
                    <span className="font-medium text-purple-600 dark:text-purple-400">
                      -{currencySymbol}{' '}
                      {existingInvoice.payments
                        .reduce((sum: number, p: { amount: number }) => sum + Number(p.amount), 0)
                        .toLocaleString('en', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                    </span>
                  </div>
                )}

                {/* Outstanding Balance */}
                <div className="border-t border-gray-200 pt-2 dark:border-gray-700">
                  <div className="flex items-center justify-between text-sm font-semibold">
                    <span className="text-gray-900 dark:text-gray-100">Outstanding Balance</span>
                    <span
                      className={
                        existingInvoice.outstandingBalance > 0
                          ? 'text-red-600 dark:text-red-400'
                          : 'text-green-600 dark:text-green-400'
                      }
                    >
                      {currencySymbol}{' '}
                      {Number(existingInvoice.outstandingBalance).toLocaleString('en', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                </div>
              </div>

              {/* Detail lists */}
              {((invoiceCreditNotes &&
                invoiceCreditNotes.filter((cn) => cn.status !== 'voided').length > 0) ||
                (invoiceWriteOffs &&
                  invoiceWriteOffs.filter((wo) => wo.status !== 'voided').length > 0) ||
                (appliedAdvances && appliedAdvances.length > 0) ||
                (existingInvoice.payments && existingInvoice.payments.length > 0)) && (
                <div className="mt-4 space-y-3">
                  {/* Payments detail */}
                  {existingInvoice.payments && existingInvoice.payments.length > 0 && (
                    <div>
                      <h4 className="text-xs font-medium uppercase text-gray-500 mb-1">Payments</h4>
                      <div className="divide-y divide-border dark:divide-border-dark">
                        {existingInvoice.payments.map(
                          (p: {
                            id: string;
                            amount: number;
                            paymentMethod: string;
                            paymentDate: string;
                            referenceNumber: string | null;
                          }) => (
                            <div key={p.id} className="flex items-center justify-between py-2">
                              <div>
                                <span className="text-sm font-medium text-gray-900 dark:text-gray-100 capitalize">
                                  {p.paymentMethod?.replace(/_/g, ' ') || 'Payment'}
                                </span>
                                <span className="ml-2 text-xs text-gray-500">
                                  {p.paymentDate
                                    ? new Date(p.paymentDate).toLocaleDateString()
                                    : ''}
                                </span>
                                {p.referenceNumber && (
                                  <span className="ml-2 text-xs text-gray-400">
                                    Ref: {p.referenceNumber}
                                  </span>
                                )}
                              </div>
                              <span className="text-sm font-semibold text-purple-600 dark:text-purple-400">
                                -{currencySymbol}{' '}
                                {Number(p.amount).toLocaleString('en', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </span>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  )}

                  {/* Credit Notes detail */}
                  {invoiceCreditNotes &&
                    invoiceCreditNotes.filter((cn) => cn.status !== 'voided').length > 0 && (
                      <div>
                        <h4 className="text-xs font-medium uppercase text-gray-500 mb-1">
                          Credit Notes
                        </h4>
                        <div className="divide-y divide-border dark:divide-border-dark">
                          {invoiceCreditNotes
                            .filter((cn) => cn.status !== 'voided')
                            .map((cn) => (
                              <div key={cn.id} className="flex items-center justify-between py-2">
                                <div>
                                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {cn.creditNoteNumber}
                                  </span>
                                  <span className="ml-2 text-xs text-gray-500">
                                    {cn.createdAt
                                      ? new Date(cn.createdAt).toLocaleDateString()
                                      : ''}
                                  </span>
                                  {cn.reason && (
                                    <span className="ml-2 text-xs text-gray-400">
                                      — {cn.reason}
                                    </span>
                                  )}
                                </div>
                                <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                                  -{currencySymbol}{' '}
                                  {cn.amount.toLocaleString('en', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </span>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                  {/* Write-Offs detail */}
                  {invoiceWriteOffs &&
                    invoiceWriteOffs.filter((wo) => wo.status !== 'voided').length > 0 && (
                      <div>
                        <h4 className="text-xs font-medium uppercase text-gray-500 mb-1">
                          Write-Offs
                        </h4>
                        <div className="divide-y divide-border dark:divide-border-dark">
                          {invoiceWriteOffs
                            .filter((wo) => wo.status !== 'voided')
                            .map((wo) => (
                              <div key={wo.id} className="flex items-center justify-between py-2">
                                <div>
                                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {wo.writeOffNumber}
                                  </span>
                                  <span className="ml-2 text-xs text-gray-500">
                                    {wo.createdAt
                                      ? new Date(wo.createdAt).toLocaleDateString()
                                      : ''}
                                  </span>
                                  {wo.reason && (
                                    <span className="ml-2 text-xs text-gray-400">
                                      — {wo.reason}
                                    </span>
                                  )}
                                </div>
                                <span className="text-sm font-semibold text-orange-600 dark:text-orange-400">
                                  -{currencySymbol}{' '}
                                  {wo.amount.toLocaleString('en', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </span>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                  {/* Advances detail */}
                  {appliedAdvances && appliedAdvances.length > 0 && (
                    <div>
                      <h4 className="text-xs font-medium uppercase text-gray-500 mb-1">
                        Advances Applied
                      </h4>
                      <div className="divide-y divide-border dark:divide-border-dark">
                        {appliedAdvances.map((app) => (
                          <div key={app.id} className="flex items-center justify-between py-2">
                            <div>
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {app.advance_number}
                              </span>
                              <span className="ml-2 text-xs text-gray-500">
                                {new Date(app.applied_at).toLocaleDateString()}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                                -{currencySymbol}{' '}
                                {Number(app.amount).toLocaleString('en', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </span>
                              {(isAdmin || can(PERMISSIONS.BILLING_CREATE_ADVANCE)) &&
                                existingInvoice?.status !== 'void' && (
                                  <button
                                    type="button"
                                    className="rounded px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                                    onClick={() => {
                                      if (
                                        window.confirm(
                                          `Remove advance ${app.advance_number} ($${Number(app.amount).toFixed(2)}) from this invoice? This will restore the advance balance and update the invoice outstanding amount.`,
                                        )
                                      ) {
                                        apiClient
                                          .delete(`/billing/advances/applications/${app.id}`)
                                          .then(() => {
                                            toast({
                                              variant: 'success',
                                              title: 'Advance removed from invoice',
                                            });
                                            queryClient.refetchQueries({
                                              queryKey: ['billing', 'invoices', editId],
                                            });
                                            queryClient.refetchQueries({ queryKey: ['billing'] });
                                          })
                                          .catch(() => {
                                            toast({
                                              variant: 'error',
                                              title: 'Failed to remove advance',
                                            });
                                          });
                                      }
                                    }}
                                  >
                                    Remove
                                  </button>
                                )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {!appliedAdvances?.length &&
                !invoiceCreditNotes?.length &&
                !invoiceWriteOffs?.length && (
                  <p className="mt-4 text-center text-sm text-gray-500 dark:text-gray-400">
                    No adjustments applied to this invoice.
                  </p>
                )}
            </CardContent>
          </Card>
        )}

        {/* Notes & Reminders — always editable, gated by permission */}
        {can(PERMISSIONS.NOTE_CATEGORIES_READ) && (
          <Card>
            <CardHeader
              title="Notes & Reminders"
              description="Add notes with optional categories and reminders"
              action={
                can(PERMISSIONS.NOTE_CATEGORIES_CREATE) ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setShowNoteForm(!showNoteForm)}
                  >
                    {showNoteForm ? 'Cancel' : 'Add Note'}
                  </Button>
                ) : undefined
              }
            />
            <CardContent>
              {showNoteForm && (
                <div className="mb-4 rounded-lg border border-border p-4 dark:border-border-dark space-y-3">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Category
                    </label>
                    <select
                      value={newNoteCategory}
                      onChange={(e) => setNewNoteCategory(e.target.value)}
                      className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm dark:border-border-dark dark:bg-gray-800 dark:text-gray-100"
                    >
                      <option value="">Select category</option>
                      {noteCategories?.map((cat) => (
                        <option key={cat.id} value={cat.name.toLowerCase().replace(/\s+/g, '_')}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Content <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={newNoteContent}
                      onChange={(e) => setNewNoteContent(e.target.value)}
                      rows={3}
                      maxLength={10000}
                      placeholder="Type your note..."
                      className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm dark:border-border-dark dark:bg-gray-800 dark:text-gray-100"
                    />
                  </div>
                  {isSchedulingCategory && (
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Reminder Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="datetime-local"
                        value={newNoteReminderDate}
                        onChange={(e) => setNewNoteReminderDate(e.target.value)}
                        min={new Date().toISOString().slice(0, 16)}
                        className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm dark:border-border-dark dark:bg-gray-800 dark:text-gray-100"
                      />
                    </div>
                  )}
                  {isSchedulingCategory && (
                    <div className="relative">
                      <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Assign Reminder To
                      </label>
                      <input
                        type="text"
                        value={newNoteAssigneeSearch}
                        onChange={(e) => {
                          setNewNoteAssigneeSearch(e.target.value);
                          setShowAssigneeDropdown(true);
                          if (!e.target.value.trim()) setNewNoteAssigneeId('');
                        }}
                        onFocus={() => setShowAssigneeDropdown(true)}
                        onBlur={() => setTimeout(() => setShowAssigneeDropdown(false), 200)}
                        placeholder="Search user (leave empty for yourself)..."
                        className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm dark:border-border-dark dark:bg-gray-800 dark:text-gray-100 placeholder:text-gray-400"
                        autoComplete="off"
                      />
                      {showAssigneeDropdown && staffUsers && staffUsers.length > 0 && (
                        <ul className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded-lg border border-border bg-white shadow-lg dark:border-border-dark dark:bg-gray-800">
                          {(staffUsers ?? [])
                            .filter((u) => {
                              if (!newNoteAssigneeSearch.trim()) return true;
                              const q = newNoteAssigneeSearch.toLowerCase();
                              return (
                                u.email.toLowerCase().includes(q) ||
                                u.name.toLowerCase().includes(q)
                              );
                            })
                            .slice(0, 15)
                            .map((user) => (
                              <li key={user.id}>
                                <button
                                  type="button"
                                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    setNewNoteAssigneeId(user.id);
                                    setNewNoteAssigneeSearch(user.name || user.email);
                                    setShowAssigneeDropdown(false);
                                  }}
                                >
                                  <span className="font-medium text-gray-900 dark:text-gray-100">
                                    {user.name || user.email}
                                  </span>
                                </button>
                              </li>
                            ))}
                        </ul>
                      )}
                      <p className="mt-1 text-xs text-gray-500">
                        Optional. Leave empty to assign to yourself.
                      </p>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={newNoteClientVisible}
                        onChange={(e) => setNewNoteClientVisible(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500 dark:border-gray-600 dark:bg-surface-dark-secondary"
                      />
                      <span>Send to client</span>
                    </label>
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleAddPendingNote}
                      disabled={!newNoteContent.trim() || savingNote}
                      loading={savingNote}
                    >
                      Add Note
                    </Button>
                  </div>
                </div>
              )}

              {/* Saved notes (edit mode) */}
              {isEditMode && savedNotes && savedNotes.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs font-medium uppercase text-gray-500 mb-2">Saved Notes</h4>
                  <ul className="divide-y divide-border dark:divide-border-dark">
                    {savedNotes.map((note) => (
                      <li key={note.id} className="py-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-2 flex-1 min-w-0">
                            <div
                              className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                                note.senderType === 'client'
                                  ? 'bg-brand-600 text-white dark:bg-brand-500'
                                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                              }`}
                            >
                              {note.senderType === 'client' ? 'C' : 'S'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                {note.senderType === 'client' && (
                                  <span className="inline-flex rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
                                    Client
                                  </span>
                                )}
                                {note.category && (
                                  <span className="inline-flex rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700 dark:bg-brand-900/20 dark:text-brand-300">
                                    {note.category.replace(/_/g, ' ')}
                                  </span>
                                )}
                                {note.clientVisible && note.senderType !== 'client' && (
                                  <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/20 dark:text-green-300">
                                    <svg
                                      className="h-3 w-3"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                      strokeWidth={2}
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M5 13l4 4L19 7"
                                      />
                                    </svg>
                                    Sent to Client
                                  </span>
                                )}
                                {note.createdByName && note.senderType !== 'client' && (
                                  <span className="text-xs text-gray-500">
                                    by {note.createdByName}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
                                {note.content}
                              </p>
                            </div>
                          </div>
                          <time className="shrink-0 text-xs text-gray-400">
                            {new Date(note.createdAt).toLocaleDateString()}
                          </time>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Pending notes list */}
              {pendingNotes.length > 0 ? (
                <ul className="divide-y divide-border dark:divide-border-dark">
                  {pendingNotes.map((pNote, idx) => (
                    <li key={idx} className="flex items-start justify-between py-3 gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {pNote.category && (
                            <Badge variant="default" size="sm">
                              {pNote.category.replace(/_/g, ' ')}
                            </Badge>
                          )}
                          {pNote.reminderDate && (
                            <span className="text-xs text-amber-600 dark:text-amber-400">
                              Reminder: {new Date(pNote.reminderDate).toLocaleString()}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
                          {pNote.content}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removePendingNote(idx)}
                        className="text-red-500 hover:text-red-700 text-sm shrink-0"
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              ) : !showNoteForm ? (
                <p className="text-sm text-gray-500">No notes added yet.</p>
              ) : null}
            </CardContent>
          </Card>
        )}
      </form>

      {/* Job Search Modal */}
      {showJobSearchModal && (
        <JobSearchModal
          onSelect={handleJobSearchSelect}
          onClose={() => setShowJobSearchModal(false)}
          clientId={clientId || undefined}
        />
      )}

      {/* Client Search Modal */}
      {showClientSearchModal && (
        <ClientSearchModal
          onSelect={handleClientSearchSelect}
          onClose={() => setShowClientSearchModal(false)}
          title="Search & Select Client"
        />
      )}

      {/* Staff (Partner) Search Modal */}
      {showStaffSearchModal && (
        <StaffSearchModal
          onSelect={handleStaffSearchSelect}
          onClose={() => setShowStaffSearchModal(false)}
          title="Search & Select Partner"
        />
      )}

      {/* Payment Modal */}
      {showPaymentModal && editId && existingInvoice && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl dark:bg-gray-900">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
              Record Payment
            </h2>
            <PaymentRecordForm
              invoiceId={editId}
              outstandingBalance={existingInvoice.outstandingBalance}
              onSuccess={() => {
                setShowPaymentModal(false);
                navigate('/billing');
              }}
              onCancel={() => setShowPaymentModal(false)}
            />
          </div>
        </div>
      )}

      {/* Void Confirmation Modal */}
      {showVoidModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl dark:bg-gray-900">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Void {TRANSACTION_TYPE_LABELS[txType]}
            </h2>
            <p className="mt-2 text-sm text-gray-500">
              Are you sure you want to void this {TRANSACTION_TYPE_LABELS[txType].toLowerCase()}?
              This action cannot be undone.
            </p>
            <textarea
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              placeholder="Reason for voiding..."
              className="mt-4 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              rows={3}
            />
            <div className="mt-4 flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setShowVoidModal(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="danger"
                disabled={!voidReason.trim()}
                loading={voidMutation.isPending}
                onClick={async () => {
                  if (!voidReason.trim() || !editId) return;
                  try {
                    if (txType === 'invoice') {
                      await voidMutation.mutateAsync({ invoiceId: editId, reason: voidReason });
                    } else {
                      const voidEndpoints: Record<string, string> = {
                        write_off: `/billing/write-offs/${editId}/void`,
                        credit_note: `/billing/credit-notes/${editId}/void`,
                        discount: `/billing/discounts/${editId}/void`,
                        debit_note: `/billing/debit-notes/${editId}/void`,
                      };
                      await apiClient.post(voidEndpoints[txType], { reason: voidReason.trim() });
                    }
                    toast({
                      variant: 'success',
                      title: `${TRANSACTION_TYPE_LABELS[txType]} voided`,
                    });
                    setShowVoidModal(false);
                    queryClient.refetchQueries({ queryKey: ['billing'] });
                    queryClient.invalidateQueries({ queryKey: ['debit-notes'] });
                    navigate('/billing');
                  } catch {
                    toast({
                      variant: 'error',
                      title: `Failed to void ${TRANSACTION_TYPE_LABELS[txType].toLowerCase()}`,
                    });
                  }
                }}
              >
                Void {TRANSACTION_TYPE_LABELS[txType]}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Apply Advance Modal */}
      {showAdvanceModal && editId && existingInvoice && (
        <ApplyAdvanceModal
          invoiceId={editId}
          clientId={existingInvoice.clientId}
          practiceEntityId={
            ((existingInvoice as unknown as Record<string, unknown>).practiceEntityId as string) ||
            ((existingInvoice as unknown as Record<string, unknown>)
              .practice_entity_id as string) ||
            ''
          }
          outstandingBalance={existingInvoice.outstandingBalance}
          currencySymbol={currencySymbol}
          onSuccess={() => {
            setShowAdvanceModal(false);
            queryClient.invalidateQueries({ queryKey: billingKeys.invoiceDetail(editId) });
            queryClient.invalidateQueries({ queryKey: ['billing', 'advances'] });
          }}
          onCancel={() => setShowAdvanceModal(false)}
        />
      )}

      {/* Group Invoice — bill the selected client's whole group as one invoice */}
      {showGroupInvoice && clientId && (
        <GroupInvoicePicker contactId={clientId} onClose={() => setShowGroupInvoice(false)} />
      )}

      {/* Address Modal */}
      {showAddressModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => setShowAddressModal(false)}
        >
          <div
            className="mx-4 w-full max-w-lg rounded-xl bg-white p-6 shadow-xl dark:bg-gray-800"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
              Billing & Postal Address
            </h3>
            <div className="space-y-4 max-h-[70vh] overflow-y-auto">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Billing Address
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Address Line 1"
                  value={billingAddress.addressLine1}
                  onChange={(e) => {
                    const val = e.target.value;
                    setBillingAddress((prev) => ({ ...prev, addressLine1: val }));
                    if (postalSameAsBilling)
                      setPostalAddress((prev) => ({ ...prev, addressLine1: val }));
                  }}
                  placeholder="Street address"
                />
                <Input
                  label="Address Line 2"
                  value={billingAddress.addressLine2}
                  onChange={(e) => {
                    const val = e.target.value;
                    setBillingAddress((prev) => ({ ...prev, addressLine2: val }));
                    if (postalSameAsBilling)
                      setPostalAddress((prev) => ({ ...prev, addressLine2: val }));
                  }}
                  placeholder="Apt, suite, unit"
                />
                <Input
                  label="City"
                  value={billingAddress.city}
                  onChange={(e) => {
                    const val = e.target.value;
                    setBillingAddress((prev) => ({ ...prev, city: val }));
                    if (postalSameAsBilling) setPostalAddress((prev) => ({ ...prev, city: val }));
                  }}
                />
                <Input
                  label="Province"
                  value={billingAddress.province}
                  onChange={(e) => {
                    const val = e.target.value;
                    setBillingAddress((prev) => ({ ...prev, province: val }));
                    if (postalSameAsBilling)
                      setPostalAddress((prev) => ({ ...prev, province: val }));
                  }}
                />
                <Input
                  label="Postal Code"
                  value={billingAddress.postalCode}
                  onChange={(e) => {
                    const val = e.target.value;
                    setBillingAddress((prev) => ({ ...prev, postalCode: val }));
                    if (postalSameAsBilling)
                      setPostalAddress((prev) => ({ ...prev, postalCode: val }));
                  }}
                />
                <Input
                  label="Country"
                  value={billingAddress.country}
                  onChange={(e) => {
                    const val = e.target.value;
                    setBillingAddress((prev) => ({ ...prev, country: val }));
                    if (postalSameAsBilling)
                      setPostalAddress((prev) => ({ ...prev, country: val }));
                  }}
                />
              </div>

              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="postal-same-modal"
                  checked={postalSameAsBilling}
                  onChange={(e) => {
                    setPostalSameAsBilling(e.target.checked);
                    if (e.target.checked) {
                      setPostalAddress(billingAddress);
                    }
                  }}
                  className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                />
                <label
                  htmlFor="postal-same-modal"
                  className="text-sm text-gray-700 dark:text-gray-300"
                >
                  Postal address same as billing
                </label>
              </div>

              {!postalSameAsBilling && (
                <>
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 pt-2">
                    Postal Address
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      label="Address Line 1"
                      value={postalAddress.addressLine1}
                      onChange={(e) =>
                        setPostalAddress((prev) => ({ ...prev, addressLine1: e.target.value }))
                      }
                      placeholder="Street address"
                    />
                    <Input
                      label="Address Line 2"
                      value={postalAddress.addressLine2}
                      onChange={(e) =>
                        setPostalAddress((prev) => ({ ...prev, addressLine2: e.target.value }))
                      }
                      placeholder="Apt, suite, unit"
                    />
                    <Input
                      label="City"
                      value={postalAddress.city}
                      onChange={(e) =>
                        setPostalAddress((prev) => ({ ...prev, city: e.target.value }))
                      }
                    />
                    <Input
                      label="Province"
                      value={postalAddress.province}
                      onChange={(e) =>
                        setPostalAddress((prev) => ({ ...prev, province: e.target.value }))
                      }
                    />
                    <Input
                      label="Postal Code"
                      value={postalAddress.postalCode}
                      onChange={(e) =>
                        setPostalAddress((prev) => ({ ...prev, postalCode: e.target.value }))
                      }
                    />
                    <Input
                      label="Country"
                      value={postalAddress.country}
                      onChange={(e) =>
                        setPostalAddress((prev) => ({ ...prev, country: e.target.value }))
                      }
                    />
                  </div>
                </>
              )}
            </div>
            <div className="mt-6 flex justify-end">
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() => setShowAddressModal(false)}
              >
                Done
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Apply Advance Modal ─────────────────────────────────────────────────────

function ApplyAdvanceModal({
  invoiceId,
  clientId,
  practiceEntityId,
  outstandingBalance,
  currencySymbol,
  onSuccess,
  onCancel,
}: {
  invoiceId: string;
  clientId: string;
  practiceEntityId: string;
  outstandingBalance: number;
  currencySymbol: string;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const [selectedAdvanceId, setSelectedAdvanceId] = useState('');
  const [amount, setAmount] = useState('');
  const [transactionDate, setTransactionDate] = useState(new Date().toISOString().split('T')[0]);
  const [applying, setApplying] = useState(false);
  const { toast } = useToast();

  const { data: advanceData, isLoading } = useQuery({
    queryKey: ['billing', 'advances', 'balance', clientId, practiceEntityId],
    queryFn: async () => {
      const params = new URLSearchParams({ client_id: clientId });
      if (practiceEntityId) params.set('practice_entity_id', practiceEntityId);
      const response = await apiClient.get<{
        success: boolean;
        data: {
          totalAvailable: number;
          advances: Array<{
            id: string;
            advance_number: string;
            available_balance: number;
            total: number;
          }>;
        };
      }>(`/billing/advances/balance?${params.toString()}`);
      return response.data.data;
    },
    enabled: !!clientId,
  });

  const availableAdvances = advanceData?.advances?.filter((a) => a.available_balance > 0) ?? [];
  const selectedAdvance = availableAdvances.find((a) => a.id === selectedAdvanceId);
  const maxAmount = Math.min(selectedAdvance?.available_balance ?? 0, outstandingBalance);

  const handleApply = async () => {
    const applyAmount = parseFloat(amount);
    if (!selectedAdvanceId || !applyAmount || applyAmount <= 0) {
      toast({ variant: 'error', title: 'Select an advance and enter a valid amount' });
      return;
    }
    if (applyAmount > maxAmount) {
      toast({
        variant: 'error',
        title: `Amount cannot exceed ${currencySymbol}${maxAmount.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      });
      return;
    }

    setApplying(true);
    try {
      await apiClient.post('/billing/advances/apply', {
        advance_id: selectedAdvanceId,
        invoice_id: invoiceId,
        amount: applyAmount,
        transaction_date: transactionDate,
      });
      toast({
        variant: 'success',
        title: `${currencySymbol}${applyAmount.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} advance applied to invoice`,
      });
      onSuccess();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string } } } };
      toast({
        variant: 'error',
        title: error?.response?.data?.error?.message || 'Failed to apply advance',
      });
      setApplying(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl dark:bg-gray-900">
        <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
          Apply Advance
        </h2>

        {isLoading ? (
          <p className="text-sm text-gray-500">Loading available advances...</p>
        ) : availableAdvances.length === 0 ? (
          <p className="text-sm text-gray-500">No advances available for this client.</p>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Select Advance
              </label>
              <select
                value={selectedAdvanceId}
                onChange={(e) => {
                  setSelectedAdvanceId(e.target.value);
                  setAmount('');
                }}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              >
                <option value="">Choose an advance...</option>
                {availableAdvances.map((adv) => (
                  <option key={adv.id} value={adv.id}>
                    {adv.advance_number} — Available: {currencySymbol}
                    {Number(adv.available_balance).toLocaleString('en', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </option>
                ))}
              </select>
            </div>

            {selectedAdvanceId && (
              <>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Amount to Apply (max: {currencySymbol}
                    {maxAmount.toLocaleString('en', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                    )
                  </label>
                  <input
                    type="number"
                    min={0.01}
                    max={maxAmount}
                    step="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder={maxAmount.toFixed(2)}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Invoice outstanding: {currencySymbol}
                    {outstandingBalance.toLocaleString('en', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{' '}
                    · Advance available: {currencySymbol}
                    {Number(selectedAdvance?.available_balance || 0).toLocaleString('en', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Transaction Date *
                  </label>
                  <input
                    type="date"
                    value={transactionDate}
                    onChange={(e) => setTransactionDate(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  />
                </div>
              </>
            )}
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          {availableAdvances.length > 0 && (
            <Button
              type="button"
              disabled={!selectedAdvanceId || !amount || applying}
              loading={applying}
              onClick={handleApply}
            >
              Apply Advance
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
